export type SeoFaq = {
  id?: string;
  question: string;
  answer: string;
};

export type SeoInternalLink = {
  href: string;
  label: string;
};

export type SeoGuidePage = {
  slug: string;
  title: string;
  meta: string;
  keyword: string;
  audience: string;
  cluster: string;
  buyerStage: string;
  intro: string;
  checklist: string[];
  faq: SeoFaq[];
  internalLinks: SeoInternalLink[];
  ctaLabel: string;
};

const coreLinks: SeoInternalLink[] = [
  { href: '/producten/', label: 'Bekijk prelaunch producten' },
  { href: '/keuzehulp/', label: 'Start keuzehulp' },
  { href: '/pickleball-clubpakket/', label: 'Vraag clubpakket advies' }
];

export const seoGuidePages: SeoGuidePage[] = [
  {
    slug: '/pickleball-kopen/',
    title: 'Pickleball kopen in Nederland en Belgie',
    meta: 'Keuzehulp voor pickleball kopen in Nederland en Belgie: sets, paddles, ballen, netten en clubpakketten zonder fake stock.',
    keyword: 'pickleball kopen',
    audience: 'Beginners, recreanten, clubs en scholen',
    cluster: 'koopintentie',
    buyerStage: 'ready-to-buy maar prelaunch gated',
    intro: 'Begin met de speelsituatie: indoor of outdoor, alleen of met groep, recreatief of clubniveau. ProPickleball.nl helpt eerst kiezen en activeert checkout pas na supplier proof.',
    checklist: ['Kies eerst set, paddle of clubpakket.', 'Controleer indoor/outdoor bal en ondergrond.', 'Vraag club- of schooladvies aan bij grotere aantallen.'],
    faq: [
      { question: 'Kan ik nu al pickleball producten bestellen?', answer: 'Nee. ProPickleball.nl staat in prelaunch. Je kunt producten bekijken, keuzehulp gebruiken en prijsalert of offerte-interesse tonen.' },
      { question: 'Waarom staat checkout nog uit?', answer: 'Checkout blijft uit tot leverancier, GTIN/EAN, GPSR, voorraad, betaling, verzending en retourroute aantoonbaar klaar zijn.' },
      { question: 'Welke producten komen eerst?', answer: 'Startersets, paddles, ballen, portable nets, grips, tassen en club/schoolpakketten krijgen prioriteit.' }
    ],
    internalLinks: [
      { href: '/pickleball-set-kopen/', label: 'Pickleball set kiezen' },
      { href: '/pickleball-paddle-kopen/', label: 'Pickleball paddle kiezen' },
      { href: '/pickleball-ballen-kopen/', label: 'Pickleball ballen kiezen' },
      ...coreLinks
    ],
    ctaLabel: 'Start keuzehulp'
  },
  {
    slug: '/pickleball-paddle-kopen/',
    title: 'Pickleball paddle kopen',
    meta: 'Kies pickleball paddles op niveau, controle, power, grip en dealerproof status.',
    keyword: 'pickleball paddle',
    audience: 'Beginners tot gevorderde spelers',
    cluster: 'paddles',
    buyerStage: 'solution-aware',
    intro: 'Een goede paddle past bij je speelstijl. Beginners hebben vooral controle en vergevingsgezindheid nodig; gevorderden kunnen later naar premium modellen na dealerautorisatie.',
    checklist: ['Let op gewicht, grip en vorm.', 'Kies controle voordat je power koopt.', 'Premium merkpaddles blijven verificatie-first.'],
    faq: [
      { question: 'Wat is het verschil tussen een paddle en een racket?', answer: 'Bij pickleball wordt meestal paddle gebruikt: een dicht slagvlak zonder snaren. In Nederland zoeken veel mensen ook op racket.' },
      { question: 'Welke paddle past bij beginners?', answer: 'Beginners hebben meestal meer aan controle, vergevingsgezindheid en comfortabele grip dan aan maximale power.' },
      { question: 'Waarom zijn premium paddles nog gated?', answer: 'Voor premium merken is dealerautorisatie nodig voordat er verkoop-, voorraad- of dealerclaims mogen staan.' }
    ],
    internalLinks: [
      { href: '/pickleball-paddle-beginners/', label: 'Paddle voor beginners' },
      { href: '/collecties/paddles/', label: 'Prelaunch paddles bekijken' },
      { href: '/pickleball-kopen/', label: 'Algemene koopgids' },
      ...coreLinks
    ],
    ctaLabel: 'Kies paddle'
  },
  {
    slug: '/pickleball-set-kopen/',
    title: 'Pickleball set kopen',
    meta: 'Startersets voor 2 of 4 spelers, schoolintro en recreatief gebruik.',
    keyword: 'pickleball set',
    audience: 'Gezinnen, scholen, beginners en vakantieparken',
    cluster: 'startersets',
    buyerStage: 'ready-to-buy',
    intro: 'Een starterset is de snelste manier om te beginnen, maar de inhoud moet passen bij het aantal spelers, de ondergrond en het gebruiksmoment.',
    checklist: ['2 spelers voor thuis of proefspel.', '4 spelers voor gezin, les of clubintro.', 'Controleer of ballen passen bij indoor of outdoor.'],
    faq: [
      { question: 'Wat zit er in een goede pickleball set?', answer: 'Een set bevat minimaal paddles en ballen. Voor scholen, clubs en buitengebruik is vaak ook een net en belijning nodig.' },
      { question: 'Is een 2-spelers of 4-spelers set beter?', answer: 'Voor thuis testen is 2 spelers vaak genoeg. Voor gezin, les, clinic of vereniging is 4 spelers praktischer.' },
      { question: 'Kan een set indoor en outdoor werken?', answer: 'Dat hangt vooral af van de ballen en ondergrond. Daarom splitst ProPickleball de keuzehulp op indoor en outdoor.' }
    ],
    internalLinks: [
      { href: '/pickleball-starterset-kopen/', label: 'Starterset keuzehulp' },
      { href: '/collecties/startersets/', label: 'Prelaunch startersets' },
      { href: '/pickleball-voor-scholen/', label: 'Sets voor scholen' },
      ...coreLinks
    ],
    ctaLabel: 'Bekijk startersets'
  },
  {
    slug: '/pickleball-starterset-kopen/',
    title: 'Pickleball starterset kopen',
    meta: 'Kies een pickleball starterset voor 2 of 4 spelers met duidelijke indoor/outdoor uitleg en prijsalert.',
    keyword: 'pickleball starterset',
    audience: 'Nieuwe spelers, gezinnen, scholen en recreatieparken',
    cluster: 'startersets',
    buyerStage: 'ready-to-buy',
    intro: 'Een starterset verlaagt de drempel om te beginnen. De juiste keuze hangt af van aantal spelers, speelplek, baltype, netbehoefte en of het materiaal voor lessen of recreatie bedoeld is.',
    checklist: ['Kies 2 spelers voor thuis testen.', 'Kies 4 spelers voor les, gezin of clinic.', 'Voeg net en belijning toe wanneer er nog geen veld is.'],
    faq: [
      { question: 'Welke starterset is het veiligst voor scholen?', answer: 'Een schoolset moet groepsgeschikt zijn, duidelijke belijning hebben en geen losse premium onderdelen forceren zonder toezicht en instructies.' },
      { question: 'Moet een starterset een net bevatten?', answer: 'Niet altijd. Op bestaande velden niet, maar voor gymles, oprit, clubintro of recreatiepark vaak wel.' },
      { question: 'Waarom prijsalert in plaats van bestellen?', answer: 'Prijsalert meet vraag zonder te doen alsof voorraad, supplier en retourroute al rond zijn.' }
    ],
    internalLinks: [
      { href: '/collecties/startersets/', label: 'Startersets in de live drop' },
      { href: '/pickleball-set-kopen/', label: 'Set kopen keuzehulp' },
      { href: '/pickleball-materiaal-gymles/', label: 'Materiaal voor gymles' },
      ...coreLinks
    ],
    ctaLabel: 'Ontvang starterset prijsalert'
  },
  {
    slug: '/pickleball-ballen-indoor-outdoor/',
    title: 'Indoor of outdoor pickleball ballen',
    meta: 'Uitleg over indoor en outdoor pickleball ballen voor sporthal, club, oprit en buitenbaan.',
    keyword: 'pickleball ballen',
    audience: 'Recreanten, clubs en scholen',
    cluster: 'ballen',
    buyerStage: 'problem-aware',
    intro: 'De verkeerde bal geeft slecht stuitgedrag en onnodige retouren. Indoorballen zijn anders dan outdoorballen; kies op basis van hal, wind en ondergrond.',
    checklist: ['Indoor voor sporthallen en gladde ondergrond.', 'Outdoor voor buitenbanen en windbestendiger spel.', 'Vervang gescheurde of vervormde ballen.'],
    faq: [
      { question: 'Wat is het verschil tussen indoor en outdoor pickleball ballen?', answer: 'Outdoor ballen zijn meestal harder en beter bestand tegen wind. Indoor ballen spelen zachter en passen beter bij sporthallen.' },
      { question: 'Welke bal moet een club kiezen?', answer: 'Kies per speellocatie. Een club met sporthal en buitenbaan heeft vaak beide types nodig.' },
      { question: 'Zijn alle ballen toernooigoedgekeurd?', answer: 'Nee. Toernooigoedkeuring wordt alleen vermeld wanneer de leverancier dit bewijs levert.' }
    ],
    internalLinks: [
      { href: '/pickleball-ballen-kopen/', label: 'Pickleball ballen kopen' },
      { href: '/pickleball-outdoor-ballen/', label: 'Outdoor ballen' },
      { href: '/pickleball-indoor-ballen/', label: 'Indoor ballen' },
      ...coreLinks
    ],
    ctaLabel: 'Kies ballen'
  },
  {
    slug: '/pickleball-ballen-kopen/',
    title: 'Pickleball ballen kopen',
    meta: 'Keuzehulp voor pickleball ballen kopen: indoor, outdoor, clubtraining en vervangsets.',
    keyword: 'pickleball ballen kopen',
    audience: 'Spelers, trainers, clubs en scholen',
    cluster: 'ballen',
    buyerStage: 'ready-to-buy',
    intro: 'Ballen zijn de snelste herhaalaankoop in het assortiment. De koopvraag moet daarom meteen indoor/outdoor, speelplek en aantal spelers scheiden.',
    checklist: ['Kies indoor of outdoor voordat je aantallen bepaalt.', 'Plan meer ballen voor training en clinics.', 'Controleer claims zoals approved alleen met supplier proof.'],
    faq: [
      { question: 'Hoeveel ballen heb je nodig?', answer: 'Voor recreatief thuisgebruik volstaat vaak een klein setje. Voor training, clubavond of les is een grotere voorraad praktischer.' },
      { question: 'Kan ik ballen mixen met een starterset?', answer: 'Ja, ballen zijn logische add-ons bij startersets, netten en clubpakketten.' },
      { question: 'Waarom is voorraad nog niet live?', answer: 'Omdat supplier route, GTIN/EAN en productinformatie eerst bevestigd moeten zijn.' }
    ],
    internalLinks: [
      { href: '/collecties/ballen/', label: 'Prelaunch ballen bekijken' },
      { href: '/pickleball-ballen-indoor-outdoor/', label: 'Indoor versus outdoor uitleg' },
      { href: '/pickleball-clubpakket/', label: 'Ballen voor clubpakket' },
      ...coreLinks
    ],
    ctaLabel: 'Ontvang ballen prijsalert'
  },
  {
    slug: '/pickleball-outdoor-ballen/',
    title: 'Outdoor pickleball ballen',
    meta: 'Outdoor pickleball ballen voor buitenbaan, oprit, wind en clubtraining met duidelijke keuzehulp.',
    keyword: 'outdoor pickleball ballen',
    audience: 'Buitenspelers, verenigingen en recreatieparken',
    cluster: 'ballen',
    buyerStage: 'solution-aware',
    intro: 'Outdoor ballen moeten passen bij wind, ruwer oppervlak en frequenter vervangen. Voor clubs is aantallenplanning belangrijker dan een losse balkeuze.',
    checklist: ['Kies outdoor bij buitenbaan of oprit.', 'Let op zichtbaarheid van kleur en slijtage.', 'Plan bulk pas na supplier en carrier proof.'],
    faq: [
      { question: 'Zijn outdoor ballen harder?', answer: 'Vaak wel. Ze zijn bedoeld voor buitenomstandigheden en spelen anders dan indoor ballen.' },
      { question: 'Welke kleur is het beste buiten?', answer: 'Kies een kleur die zichtbaar contrasteert met baan, licht en omgeving.' },
      { question: 'Is bulkinkoop al mogelijk?', answer: 'Nog niet als checkout. Voor clubs blijft dit een offerte- en supplier-check route.' }
    ],
    internalLinks: [
      { href: '/collecties/ballen/', label: 'Ballen live drop' },
      { href: '/pickleball-net-kopen/', label: 'Buitenveld met net' },
      { href: '/pickleball-voor-verenigingen/', label: 'Outdoor voor verenigingen' },
      ...coreLinks
    ],
    ctaLabel: 'Vraag outdoor advies'
  },
  {
    slug: '/pickleball-indoor-ballen/',
    title: 'Indoor pickleball ballen',
    meta: 'Indoor pickleball ballen voor sporthal, school, gymles en clubtraining met keuzehulp.',
    keyword: 'indoor pickleball ballen',
    audience: 'Scholen, sporthallen, beginners en indoor clubs',
    cluster: 'ballen',
    buyerStage: 'solution-aware',
    intro: 'Indoor ballen moeten passen bij sporthalvloer, stuit, zichtbaarheid en groepsgebruik. Voor scholen telt ook veiligheid en eenvoudig uitleggen.',
    checklist: ['Kies indoor voor sporthal of gladde vloer.', 'Combineer met duidelijke belijning.', 'Gebruik vervangsets bij lesgroepen.'],
    faq: [
      { question: 'Kan een outdoor bal in de sporthal?', answer: 'Het kan soms, maar speelt vaak minder prettig. Indoor ballen zijn bedoeld voor halgebruik.' },
      { question: 'Welke bal past bij gymles?', answer: 'Kies een bal die zichtbaar is, voorspelbaar stuit en past bij het vaardigheidsniveau van leerlingen.' },
      { question: 'Moet de bal gecertificeerd zijn?', answer: 'Alleen wanneer je toernooi- of officiele claims publiceert. Zonder bewijs blijft de claim uit.' }
    ],
    internalLinks: [
      { href: '/pickleball-voor-scholen/', label: 'Indoor voor scholen' },
      { href: '/pickleball-materiaal-gymles/', label: 'Gymles materiaal' },
      { href: '/collecties/ballen/', label: 'Prelaunch ballen' },
      ...coreLinks
    ],
    ctaLabel: 'Vraag indoor advies'
  },
  {
    slug: '/pickleball-net-kopen/',
    title: 'Pickleball net kopen',
    meta: 'Keuzehulp voor draagbare pickleball netten, belijning en veldopbouw.',
    keyword: 'pickleball net',
    audience: 'Clubs, scholen, bedrijven en recreatieparken',
    cluster: 'netten-en-belijning',
    buyerStage: 'solution-aware',
    intro: 'Een draagbaar net is ideaal voor tijdelijke velden, maar vraagt extra aandacht voor stabiliteit, struikelrisico en bulky retourkosten.',
    checklist: ['Gebruik stabiele opbouw en duidelijke belijning.', 'Check wind en ondergrond buiten.', 'Vraag offerte aan bij club- of schoolaantallen.'],
    faq: [
      { question: 'Is een draagbaar net geschikt voor clubs?', answer: 'Ja, vooral voor tijdelijke velden, clinics en gedeelde sporthallen. Stabiliteit en onderdelenlijst moeten wel duidelijk zijn.' },
      { question: 'Waarom zijn netten quote-first?', answer: 'Netten zijn bulky. Carrierkosten, retourkosten en onderdelencontrole moeten eerst goed staan.' },
      { question: 'Heb ik ook belijning nodig?', answer: 'Alleen wanneer er geen permanente pickleballlijnen zijn. Voor scholen en clubs is duidelijke belijning meestal essentieel.' }
    ],
    internalLinks: [
      { href: '/collecties/netten-en-belijning/', label: 'Netten en belijning bekijken' },
      { href: '/pickleball-clubpakket/', label: 'Net in clubpakket' },
      { href: '/pickleball-voor-scholen/', label: 'Netten voor scholen' },
      ...coreLinks
    ],
    ctaLabel: 'Vraag netadvies'
  },
  {
    slug: '/pickleball-paddle-beginners/',
    title: 'Pickleball paddle voor beginners',
    meta: 'Welke pickleball paddle past bij beginners? Keuzehulp voor controle, grip, gewicht en budget.',
    keyword: 'beste pickleball paddle beginner',
    audience: 'Nieuwe spelers en overstappers van tennis of padel',
    cluster: 'paddles',
    buyerStage: 'problem-aware',
    intro: 'Beginners hebben vooral baat bij een paddle die voorspelbaar speelt. Controle, comfort en duidelijke gripkeuze zijn belangrijker dan een premium merkverhaal.',
    checklist: ['Kies controle en comfort boven extreme power.', 'Let op gripmaat en gewicht.', 'Gebruik premium merkclaims pas na autorisatie.'],
    faq: [
      { question: 'Welke paddle is beginnerproof?', answer: 'Een vergevingsgezinde paddle met comfortabele grip en controlegerichte respons is meestal de beste eerste stap.' },
      { question: 'Moet een beginner carbon kopen?', answer: 'Niet per se. Carbon kan interessant zijn, maar materiaalclaims moeten kloppen en starterwaarde is belangrijker.' },
      { question: 'Wat als ik van tennis of padel kom?', answer: 'Dan kan gripgevoel en handvatlengte belangrijker zijn. De keuzehulp filtert hierop.' }
    ],
    internalLinks: [
      { href: '/collecties/paddles/', label: 'Paddles in de live drop' },
      { href: '/pickleball-paddle-kopen/', label: 'Paddle koopgids' },
      { href: '/keuzehulp/', label: 'Doe de paddle keuzehulp' },
      ...coreLinks
    ],
    ctaLabel: 'Vind beginner paddle'
  },
  {
    slug: '/pickleball-nederland/',
    title: 'Pickleball Nederland',
    meta: 'Pickleball groeit in Nederland. Vind keuzehulp voor spelers, verenigingen en scholen.',
    keyword: 'pickleball Nederland',
    audience: 'Nederlandse spelers, clubs en scholen',
    cluster: 'geo-nederland',
    buyerStage: 'problem-aware',
    intro: 'Nederland heeft behoefte aan duidelijke keuzehulp, clubintro-pakketten en betrouwbare supplier routes. ProPickleball.nl bouwt die basis zonder voorraadclaims te forceren.',
    checklist: ['Focus op startersets, ballen, netten en clubpakketten.', 'Gebruik keuzehulp voor beginners.', 'Laat clubs offertevragen indienen.'],
    faq: [
      { question: 'Richt ProPickleball zich op heel Nederland?', answer: 'Ja, de positionering is landelijk met Tilburg als bedrijfs- en retourbasis.' },
      { question: 'Is er een fysieke winkel?', answer: 'Nee, er wordt geen fysieke winkelclaim gemaakt. De site is een prelaunch advies- en lead-capture webshop.' },
      { question: 'Welke Nederlandse doelgroep krijgt prioriteit?', answer: 'Beginners, recreanten, scholen, clubs en groeiende competitieve spelers.' }
    ],
    internalLinks: [
      { href: '/pickleball-tilburg/', label: 'Tilburg en Brabant context' },
      { href: '/pickleball-voor-verenigingen/', label: 'Verenigingen in Nederland' },
      { href: '/pickleball-kopen/', label: 'Pickleball kopen gids' },
      ...coreLinks
    ],
    ctaLabel: 'Bekijk NL aanpak'
  },
  {
    slug: '/pickleball-belgie/',
    title: 'Pickleball Belgie',
    meta: 'Pickleball keuzehulp voor Belgie met aandacht voor levering, retouren, btw/OSS en betaalmethodes.',
    keyword: 'pickleball Belgie',
    audience: 'Belgische spelers, clubs en scholen',
    cluster: 'geo-belgie',
    buyerStage: 'problem-aware',
    intro: 'Belgie blijft gepland als NL/BE route, maar checkout gaat pas live na btw/OSS, carrierkosten, retourroute en passende betaalmethodes.',
    checklist: ['Quote-first voor clubpakketten.', 'Geen BE leverbelofte zonder carrierproof.', 'Bancontact blijft PSP-besluit voor checkout.'],
    faq: [
      { question: 'Levert ProPickleball al in Belgie?', answer: 'Nog niet als checkoutbelofte. Belgie en Vlaanderen staan in de SEO- en offerteplanning tot carrier, btw en retourroute klaar zijn.' },
      { question: 'Komt Bancontact beschikbaar?', answer: 'Bancontact hoort bij de Mollie/PSP-check voor Belgische checkout, maar wordt pas live gezet na volledige readiness.' },
      { question: 'Waarom Vlaanderen apart noemen?', answer: 'De eerste BE-copy richt zich op Nederlandstalige zoekintentie en voorkomt te brede Franstalige beloftes.' }
    ],
    internalLinks: [
      { href: '/pickleball-vlaanderen/', label: 'Pickleball Vlaanderen' },
      { href: '/pickleball-clubpakket/', label: 'Clubpakket voor BE aanvragen' },
      { href: '/retouren/', label: 'Retourbeleid bekijken' },
      ...coreLinks
    ],
    ctaLabel: 'Vraag BE advies'
  },
  {
    slug: '/pickleball-vlaanderen/',
    title: 'Pickleball Vlaanderen',
    meta: 'Keuzehulp voor pickleball in Vlaanderen: startersets, paddles, ballen, netten en clubpakketten als prelaunch route.',
    keyword: 'pickleball Vlaanderen',
    audience: 'Vlaamse spelers, scholen, clubs en recreatieorganisaties',
    cluster: 'geo-belgie',
    buyerStage: 'problem-aware',
    intro: 'Vlaanderen is de logische eerste Belgische SEO-GEO route voor ProPickleball.nl. De pagina is advies- en offertegericht totdat Belgische betaling, levering en retouren operationeel bewezen zijn.',
    checklist: ['Gebruik Nederlandstalige product- en servicecopy.', 'Houd BE checkout gated tot Bancontact/carrierproof.', 'Laat clubs en scholen offertevragen indienen.'],
    faq: [
      { question: 'Is Vlaanderen onderdeel van de launch?', answer: 'Ja, als SEO- en lead-capture gebied. Live verkoop volgt pas na BE readiness.' },
      { question: 'Welke producten passen bij Vlaamse clubs?', answer: 'Startersets, ballen, netten, grips en clubpakketten zijn de eerste logische route.' },
      { question: 'Worden retourkosten vooraf getoond?', answer: 'Ja, zodra checkout live gaat moeten retourkosten en retouradres vooraf duidelijk zijn.' }
    ],
    internalLinks: [
      { href: '/pickleball-belgie/', label: 'Belgie keuzehulp' },
      { href: '/pickleball-clubpakket/', label: 'Clubpakket offerte' },
      { href: '/levering-en-verzending/', label: 'Levering en verzending' },
      ...coreLinks
    ],
    ctaLabel: 'Vraag Vlaanderen advies'
  },
  {
    slug: '/pickleball-clubpakket/',
    title: 'Pickleball clubpakket',
    meta: 'Offertehulp voor pickleball clubpakketten met paddles, ballen, netten, grips en belijning.',
    keyword: 'pickleball clubpakket',
    audience: 'Clubs, scholen, bedrijven en vakantieparken',
    cluster: 'b2b-club-school',
    buyerStage: 'ready-to-quote',
    intro: 'Clubpakketten leveren de meeste advieswaarde. De juiste mix hangt af van aantal spelers, locatie, indoor/outdoor en budget.',
    checklist: ['Aantal spelers en velden bepalen de set.', 'Indoor/outdoor bepaalt bal en belijning.', 'Levering en retourroute blijven offerte-specifiek.'],
    faq: [
      { question: 'Wat zit er in een clubpakket?', answer: 'Meestal paddles, ballen, een of meer netten, belijning, grips en soms tassen of trainingsmateriaal.' },
      { question: 'Waarom is dit quote-first?', answer: 'Aantallen, bulky transport, supplier route en productsafety verschillen per club of school.' },
      { question: 'Kan een pakket voor clinics?', answer: 'Ja, clinics en introductielessen zijn goede use cases, maar aantallen en ondergrond moeten eerst bekend zijn.' }
    ],
    internalLinks: [
      { href: '/pickleball-voor-verenigingen/', label: 'Verenigingspakket' },
      { href: '/pickleball-voor-scholen/', label: 'Schoolpakket' },
      { href: '/collecties/clubpakketten/', label: 'Clubpakketten live drop' },
      ...coreLinks
    ],
    ctaLabel: 'Vraag clubofferte'
  },
  {
    slug: '/pickleball-voor-scholen/',
    title: 'Pickleball voor scholen',
    meta: 'Pickleball pakketten en keuzehulp voor sportlessen, bewegingsonderwijs en schoolintroducties.',
    keyword: 'pickleball voor scholen',
    audience: 'Docenten LO, scholen en sportcoordinatoren',
    cluster: 'b2b-club-school',
    buyerStage: 'ready-to-quote',
    intro: 'Scholen hebben behoefte aan veilige, uitlegbare en groepsgeschikte pakketten. Daarom blijft het schoolpakket quote-first tot alle productsafety en supplier routes rond zijn.',
    checklist: ['Kies groepssets boven losse premium paddles.', 'Let op toezicht, belijning en struikelrisico.', 'Vraag pakketadvies per klasgrootte.'],
    faq: [
      { question: 'Waarom is pickleball geschikt voor scholen?', answer: 'Het is toegankelijk, snel uit te leggen en past goed in lessen rond terugslagspelen wanneer materiaal en veiligheid kloppen.' },
      { question: 'Welke veiligheidszaken tellen mee?', answer: 'Belijning, stabiele netopbouw, toezicht, voldoende ruimte en duidelijke gebruiksinstructies.' },
      { question: 'Kan een school direct bestellen?', answer: 'Nog niet. Schoolpakketten blijven offerte-first totdat supplier en GPSR dossier rond zijn.' }
    ],
    internalLinks: [
      { href: '/pickleball-materiaal-gymles/', label: 'Materiaal voor gymles' },
      { href: '/collecties/clubpakketten/', label: 'School- en clubpakketten' },
      { href: '/productveiligheid/', label: 'Productveiligheid' },
      ...coreLinks
    ],
    ctaLabel: 'Vraag schoolpakket'
  },
  {
    slug: '/pickleball-voor-verenigingen/',
    title: 'Pickleball voor verenigingen',
    meta: 'Materiaaladvies voor pickleball verenigingen: clubpakketten, netten, ballen, paddles en clinics.',
    keyword: 'pickleball voor verenigingen',
    audience: 'Sportverenigingen, tennisclubs, padelclubs en buurtsport',
    cluster: 'b2b-club-school',
    buyerStage: 'ready-to-quote',
    intro: 'Verenigingen zoeken vaak geen losse paddle, maar een startklare materiaalroute voor meerdere spelers en velden. Daarom combineert deze pagina keuzehulp met offerte-intake.',
    checklist: ['Bepaal spelersaantal en aantal velden.', 'Scheid indoor en outdoor gebruik.', 'Houd premium merkvoorraad uit tot autorisatie bevestigd is.'],
    faq: [
      { question: 'Welke verenigingen passen bij pickleball?', answer: 'Tennis-, padel-, omnisport- en buurtsportverenigingen kunnen pickleball relatief laagdrempelig testen.' },
      { question: 'Wat moet een vereniging eerst bepalen?', answer: 'Aantal spelers, locatie, veldopbouw, baltype, budget en of materiaal voor clinics of vaste training nodig is.' },
      { question: 'Kan ProPickleball advies geven zonder checkout?', answer: 'Ja. De prelaunch route is juist bedoeld om vraag en pakketwensen te kwalificeren voordat voorraad wordt opgebouwd.' }
    ],
    internalLinks: [
      { href: '/pickleball-clubpakket/', label: 'Clubpakket offerte' },
      { href: '/pickleball-net-kopen/', label: 'Netten voor verenigingen' },
      { href: '/pickleball-nederland/', label: 'Pickleball Nederland' },
      ...coreLinks
    ],
    ctaLabel: 'Vraag verenigingsadvies'
  },
  {
    slug: '/pickleball-materiaal-gymles/',
    title: 'Pickleball materiaal voor gymles',
    meta: 'Keuzehulp voor pickleball materiaal in de gymles: veilige sets, ballen, netten en belijning voor scholen.',
    keyword: 'pickleball gymles materiaal',
    audience: 'Docenten LO en bewegingsonderwijs',
    cluster: 'b2b-club-school',
    buyerStage: 'ready-to-quote',
    intro: 'Voor gymles telt niet alleen prijs, maar ook uitleg, veiligheid, opbouwtijd en groepsgrootte. Deze route vertaalt webshopproducten naar een praktische lescontext.',
    checklist: ['Kies robuuste groepssets.', 'Voorkom struikelrisico door duidelijke belijning.', 'Gebruik offerte-intake voor klasgrootte en haltype.'],
    faq: [
      { question: 'Wat heb je nodig voor een gymles pickleball?', answer: 'Paddles, indoorballen, netten of bestaande netopstelling, belijning en duidelijke uitleg voor groepswissels.' },
      { question: 'Zijn outdoor ballen geschikt voor gymles?', answer: 'Meestal zijn indoorballen logischer in sporthallen. Outdoorballen spelen anders en kunnen te hard of onvoorspelbaar aanvoelen.' },
      { question: 'Kan de school een offerte aanvragen?', answer: 'Ja, dat is de gewenste route zolang checkout en supplierdossiers nog niet live zijn.' }
    ],
    internalLinks: [
      { href: '/pickleball-voor-scholen/', label: 'Pickleball voor scholen' },
      { href: '/pickleball-indoor-ballen/', label: 'Indoor ballen kiezen' },
      { href: '/productveiligheid/', label: 'Veiligheidsinformatie' },
      ...coreLinks
    ],
    ctaLabel: 'Vraag gymles pakket'
  },
  {
    slug: '/pickleball-tilburg/',
    title: 'Pickleball Tilburg',
    meta: 'ProPickleball.nl heeft Tilburg als bedrijfs- en retourbasis en richt zich op advies voor NL en Vlaanderen.',
    keyword: 'pickleball Tilburg',
    audience: 'Tilburg, Brabant, clubs, scholen en lokale spelers',
    cluster: 'geo-lokaal',
    buyerStage: 'local-research',
    intro: 'Tilburg is de bedrijfs- en retourbasis van ProPickleball.nl. Deze pagina maakt de lokale entiteit duidelijk zonder fysieke winkel of voorraadclaim te maken.',
    checklist: ['Gebruik Tilburg als bedrijfscontext.', 'Maak geen winkelvoorraadclaim.', 'Link door naar scholen, clubs en keuzehulp.'],
    faq: [
      { question: 'Heeft ProPickleball een winkel in Tilburg?', answer: 'Nee. Tilburg is de bedrijfs- en retourbasis, niet een gepubliceerde fysieke winkel met openingstijden.' },
      { question: 'Kunnen Tilburgse clubs advies vragen?', answer: 'Ja, via de club- en schoolintake kan materiaaladvies worden voorbereid.' },
      { question: 'Waarom is deze pagina nuttig?', answer: 'Ze maakt de lokale entiteit en bedrijfsgegevens duidelijk voor gebruikers en zoekmachines zonder overclaim.' }
    ],
    internalLinks: [
      { href: '/contact/', label: 'Contactgegevens' },
      { href: '/pickleball-brabant/', label: 'Pickleball Brabant' },
      { href: '/pickleball-clubpakket/', label: 'Clubpakket advies' },
      ...coreLinks
    ],
    ctaLabel: 'Vraag Tilburg advies'
  },
  {
    slug: '/pickleball-brabant/',
    title: 'Pickleball Brabant',
    meta: 'Keuzehulp en offerte-intake voor pickleball materiaal in Brabant, met Tilburg als bedrijfsbasis.',
    keyword: 'pickleball Brabant',
    audience: 'Brabantse clubs, scholen, recreanten en organisatoren',
    cluster: 'geo-lokaal',
    buyerStage: 'local-research',
    intro: 'Brabant krijgt een regionale adviesroute voor scholen, clubs en recreatieorganisaties die pickleball willen testen zonder direct grote voorraden te kopen.',
    checklist: ['Positioneer als regionale adviesroute.', 'Stuur clubs naar offerte-intake.', 'Houd levering en voorraad gated.'],
    faq: [
      { question: 'Is dit een lokale winkelpagina?', answer: 'Nee. Het is een regionale advies- en keuzehulppagina voor Brabant.' },
      { question: 'Welke doelgroep in Brabant krijgt prioriteit?', answer: 'Scholen, sportverenigingen, recreatieparken en introductieclinics.' },
      { question: 'Wanneer wordt levering actief?', answer: 'Pas na carrier-, supplier-, retour- en checkout-readiness.' }
    ],
    internalLinks: [
      { href: '/pickleball-tilburg/', label: 'Tilburg bedrijfsbasis' },
      { href: '/pickleball-voor-verenigingen/', label: 'Verenigingen' },
      { href: '/pickleball-voor-scholen/', label: 'Scholen' },
      ...coreLinks
    ],
    ctaLabel: 'Vraag Brabant advies'
  },
  {
    slug: '/pickleball-spelregels/',
    title: 'Pickleball spelregels voor beginners',
    meta: 'Leer de basisregels van pickleball en koppel ze aan het juiste materiaal voor thuis, sporthal, club of school.',
    keyword: 'pickleball spelregels',
    audience: 'Beginners, scholen, clubs en recreanten',
    cluster: 'regels-en-veld',
    buyerStage: 'problem-aware',
    intro: 'Wie de regels begrijpt, koopt beter materiaal. Deze gids legt de basis uit zonder te doen alsof je meteen toernooimateriaal nodig hebt.',
    checklist: ['Start met dubbelspel en rally-opbouw.', 'Gebruik duidelijke belijning en passende bal.', 'Kies materiaal op leerfase, niet op premium claims.'],
    faq: [
      { question: 'Is pickleball makkelijk te leren?', answer: 'Ja, de basis is laagdrempelig. De juiste bal, duidelijke lijnen en een stabiel net maken de eerste les of rally veel beter.' },
      { question: 'Moet ik de officiele regels kennen voor recreatief spel?', answer: 'Niet volledig. Voor thuis, school en clinic is een eenvoudige uitleg genoeg, zolang veld, net en veiligheid kloppen.' },
      { question: 'Welke producten horen bij regels leren?', answer: 'Starterset, indoor of outdoor ballen, belijning en soms een draagbaar net.' }
    ],
    internalLinks: [
      { href: '/pickleball-afmetingen-veld/', label: 'Veldafmetingen bekijken' },
      { href: '/pickleball-starterset-kopen/', label: 'Starterset kiezen' },
      { href: '/pickleball-materiaal-gymles/', label: 'Gymles materiaal' },
      ...coreLinks
    ],
    ctaLabel: 'Start met regels en set'
  },
  {
    slug: '/pickleball-afmetingen-veld/',
    title: 'Pickleball veld afmetingen en belijning',
    meta: 'Praktische uitleg over pickleball veld, lijnen, net en tijdelijke opbouw voor sporthal, school en buitenbaan.',
    keyword: 'pickleball veld afmetingen',
    audience: 'Scholen, clubs, recreanten en organisatoren',
    cluster: 'regels-en-veld',
    buyerStage: 'problem-aware',
    intro: 'Veldopbouw bepaalt welke netten, lijnen en ballen logisch zijn. Voor tijdelijke velden is veiligheid belangrijker dan een snelle aankoop.',
    checklist: ['Bepaal of er permanente lijnen zijn.', 'Gebruik duidelijke tijdelijke belijning.', 'Controleer netstabiliteit en struikelrisico.'],
    faq: [
      { question: 'Heb je speciale belijning nodig?', answer: 'Alleen wanneer er geen pickleballlijnen aanwezig zijn. Tijdelijke belijning moet zichtbaar zijn en geen onnodig struikel- of sliprisico geven.' },
      { question: 'Kan pickleball in een sporthal?', answer: 'Ja, met passende indoorbal, duidelijke lijnen en voldoende ruimte rondom het veld.' },
      { question: 'Waarom zijn netten quote-first?', answer: 'Draagbare netten zijn bulky en vragen carrier-, onderdelen- en retourcontrole voordat checkout veilig is.' }
    ],
    internalLinks: [
      { href: '/pickleball-net-kopen/', label: 'Netten kiezen' },
      { href: '/collecties/netten-en-belijning/', label: 'Netten en belijning bekijken' },
      { href: '/productveiligheid/', label: 'Veiligheidsinformatie' },
      ...coreLinks
    ],
    ctaLabel: 'Plan je veld'
  },
  {
    slug: '/pickleball-hoe-beginnen/',
    title: 'Beginnen met pickleball',
    meta: 'Stap voor stap beginnen met pickleball: regels, starterset, paddle, ballen, net en keuzehulp.',
    keyword: 'beginnen met pickleball',
    audience: 'Nieuwe spelers, gezinnen, scholen en clubs',
    cluster: 'koopintentie',
    buyerStage: 'problem-aware',
    intro: 'Begin niet met losse productvergelijkingen. Begin met waar je speelt, met hoeveel spelers en of je een veld of net nodig hebt.',
    checklist: ['Leer de basisregels.', 'Kies indoor of outdoor.', 'Start met set of clubpakket voordat je premium paddles vergelijkt.'],
    faq: [
      { question: 'Wat heb je minimaal nodig?', answer: 'Paddles, ballen en een geschikt speelveld. Als er geen net of lijnen zijn, heb je ook een net en belijning nodig.' },
      { question: 'Wat is de goedkoopste start?', answer: 'Een eenvoudige set kan genoeg zijn voor thuis testen. Voor lessen of clubs is een pakket vaak slimmer dan losse aankopen.' },
      { question: 'Waarom eerst keuzehulp?', answer: 'Omdat indoor/outdoor, aantal spelers en speelniveau bepalen welke producten passen.' }
    ],
    internalLinks: [
      { href: '/pickleball-spelregels/', label: 'Spelregels leren' },
      { href: '/pickleball-set-kopen/', label: 'Set kiezen' },
      { href: '/keuzehulp/', label: 'Keuzehulp starten' },
      ...coreLinks
    ],
    ctaLabel: 'Begin met keuzehulp'
  },
  {
    slug: '/pickleball-vs-padel/',
    title: 'Pickleball of padel: wat heb je nodig?',
    meta: 'Vergelijk pickleball met padel en tennis op materiaal, veld, niveau en startersets.',
    keyword: 'pickleball vs padel',
    audience: 'Padelspelers, tennisclubs, beginners en verenigingen',
    cluster: 'koopintentie',
    buyerStage: 'problem-aware',
    intro: 'Veel spelers kennen padel of tennis al. Pickleball vraagt ander materiaal, andere ballen en een andere veldopbouw.',
    checklist: ['Gebruik een paddle zonder snaren.', 'Kies pickleball ballen, niet padelballen.', 'Check veld en net voordat je koopt.'],
    faq: [
      { question: 'Kun je padelrackets gebruiken?', answer: 'Nee, pickleball gebruikt paddles en geperforeerde plastic ballen. Materiaalkeuze is dus anders.' },
      { question: 'Is pickleball geschikt voor tennisclubs?', answer: 'Ja, vooral als laagdrempelige extra activiteit, clinic of clubavond met tijdelijke veldopbouw.' },
      { question: 'Welke route past bij padelspelers?', answer: 'Vaak een controlegerichte paddle, outdoor ballen en duidelijke uitleg over regels en veld.' }
    ],
    internalLinks: [
      { href: '/pickleball-paddle-kopen/', label: 'Paddle kiezen' },
      { href: '/pickleball-voor-verenigingen/', label: 'Verenigingsroute' },
      { href: '/pickleball-afmetingen-veld/', label: 'Veld en belijning' },
      ...coreLinks
    ],
    ctaLabel: 'Vergelijk materiaal'
  },
  {
    slug: '/pickleball-prijzen/',
    title: 'Pickleball prijzen en budget kiezen',
    meta: 'Wat kost beginnen met pickleball? Richtprijzen voor sets, paddles, ballen, netten en clubpakketten zonder voorraadclaim.',
    keyword: 'pickleball prijzen',
    audience: 'Kopers, clubs, scholen en recreanten',
    cluster: 'koopintentie',
    buyerStage: 'solution-aware',
    intro: 'Prijsvergelijking helpt alleen als duidelijk is wat in de set zit en welke service, retourroute en productinformatie erbij horen.',
    checklist: ['Vergelijk inhoud van sets, niet alleen prijs.', 'Let op indoor/outdoor ballen en netbehoefte.', 'Vraag offerte bij groepsaantallen.'],
    faq: [
      { question: 'Waarom toont ProPickleball richtprijzen?', answer: 'Omdat definitieve prijzen pas worden gepubliceerd na supplier-, voorraad- en servicecontrole.' },
      { question: 'Wanneer is een goedkope set genoeg?', answer: 'Voor thuis testen kan een eenvoudige set voldoende zijn. Voor school of club telt duurzaamheid en groepsgeschiktheid zwaarder.' },
      { question: 'Hoe vergelijk je clubpakketten?', answer: 'Vergelijk aantal spelers, netten, ballen, belijning, reservegrips en verzend/retourafspraken.' }
    ],
    internalLinks: [
      { href: '/producten/', label: 'Richtprijzen bekijken' },
      { href: '/pickleball-clubpakket/', label: 'Clubpakket prijsroute' },
      { href: '/levering-en-verzending/', label: 'Verzendkosten bekijken' },
      ...coreLinks
    ],
    ctaLabel: 'Bekijk richtprijzen'
  },
  {
    slug: '/pickleball-clinic-materiaal/',
    title: 'Pickleball clinic materiaal',
    meta: 'Materiaalplanning voor pickleball clinics: sets, ballen, netten, belijning en groepsaantallen.',
    keyword: 'pickleball clinic materiaal',
    audience: 'Clubs, scholen, buurtsport en organisatoren',
    cluster: 'b2b-club-school',
    buyerStage: 'ready-to-quote',
    intro: 'Een clinic heeft andere eisen dan een losse aankoop. Je hebt voldoende rotatie, duidelijke opbouw en reserveballen nodig.',
    checklist: ['Bepaal spelers per ronde.', 'Plan extra ballen en grips.', 'Kies quote-first bij netten en meerdere velden.'],
    faq: [
      { question: 'Wat zit in een clinicpakket?', answer: 'Paddles, ballen, netten, belijning, grips en soms tassen of instructiemateriaal.' },
      { question: 'Waarom offerte in plaats van checkout?', answer: 'Clinics verschillen in aantallen, locatie en logistiek. Offerte voorkomt verkeerde of incomplete pakketten.' },
      { question: 'Kan dit voor bedrijven?', answer: 'Ja, bedrijfsclinics passen goed bij de offerte-intake zolang logistiek en materiaalplanning duidelijk zijn.' }
    ],
    internalLinks: [
      { href: '/pickleball-clubpakket/', label: 'Clubpakket aanvragen' },
      { href: '/pickleball-voor-scholen/', label: 'Schoolroute' },
      { href: '/pickleball-net-kopen/', label: 'Netten en velden' },
      ...coreLinks
    ],
    ctaLabel: 'Plan clinicpakket'
  },
  {
    slug: '/pickleball-starterset-2-of-4-spelers/',
    title: 'Pickleball starterset voor 2 of 4 spelers',
    meta: 'Kies tussen een 2-spelers of 4-spelers pickleball starterset voor thuis, school, clinic of clubintro.',
    keyword: 'pickleball starterset 2 spelers 4 spelers',
    audience: 'Beginners, gezinnen, scholen en clubs',
    cluster: 'startersets',
    buyerStage: 'ready-to-buy',
    intro: 'Het aantal spelers is vaak belangrijker dan het merk. Kies eerst of je samen test, met gezin speelt of een groep laat rouleren.',
    checklist: ['2 spelers voor thuis testen.', '4 spelers voor gezin of korte les.', 'Grotere groep op offertebasis.'],
    faq: [
      { question: 'Wanneer kies je 2 spelers?', answer: 'Als je thuis of met een vaste speelpartner wilt proberen.' },
      { question: 'Wanneer kies je 4 spelers?', answer: 'Als je dubbelspel, gezin, clinic of les wilt doen zonder te veel wissels.' },
      { question: 'Wanneer is een clubpakket beter?', answer: 'Bij meerdere velden, groepen of scholen is offerteadvies beter dan losse setkeuze.' }
    ],
    internalLinks: [
      { href: '/pickleball-starterset-kopen/', label: 'Starterset keuzehulp' },
      { href: '/collecties/startersets/', label: 'Startersets bekijken' },
      { href: '/pickleball-clubpakket/', label: 'Groepspakket aanvragen' },
      ...coreLinks
    ],
    ctaLabel: 'Kies setgrootte'
  },
  {
    slug: '/pickleball-ballen-vervangen/',
    title: 'Wanneer pickleball ballen vervangen?',
    meta: 'Herken wanneer indoor of outdoor pickleball ballen vervangen moeten worden voor training, clubavond of gymles.',
    keyword: 'pickleball ballen vervangen',
    audience: 'Clubs, scholen, trainers en recreanten',
    cluster: 'ballen',
    buyerStage: 'problem-aware',
    intro: 'Ballen slijten sneller dan veel beginners verwachten. Een goede vervangroute voorkomt slecht spel en onnodige klachten.',
    checklist: ['Vervang gescheurde of vervormde ballen.', 'Houd indoor en outdoor apart.', 'Plan voorraad voor training en clinics.'],
    faq: [
      { question: 'Hoe zie je dat een bal vervangen moet worden?', answer: 'Let op scheuren, vervorming, onvoorspelbare stuit en sterk kleurverlies.' },
      { question: 'Hebben clubs meer ballen nodig?', answer: 'Ja, door training en clinics is vervangvoorraad belangrijker dan bij thuisgebruik.' },
      { question: 'Waarom staat dit in de keuzehulp?', answer: 'Omdat ballen een logische herhaalaankoop zijn en vaak samen met startersets of clubpakketten worden gekozen.' }
    ],
    internalLinks: [
      { href: '/pickleball-ballen-kopen/', label: 'Ballen kopen gids' },
      { href: '/pickleball-outdoor-ballen/', label: 'Outdoor ballen' },
      { href: '/pickleball-indoor-ballen/', label: 'Indoor ballen' },
      ...coreLinks
    ],
    ctaLabel: 'Kies vervangballen'
  },
  {
    slug: '/pickleball-paddle-grip/',
    title: 'Pickleball paddle grip kiezen',
    meta: 'Kies grip, overgrip en tape voor pickleball paddles op comfort, controle en gebruiksfrequentie.',
    keyword: 'pickleball grip',
    audience: 'Beginners, recreanten en groeiende spelers',
    cluster: 'paddles',
    buyerStage: 'problem-aware',
    intro: 'Grip bepaalt controle en comfort. Voor beginners is een passende grip vaak nuttiger dan meteen een duurdere paddle.',
    checklist: ['Let op gripomtrek en handgevoel.', 'Gebruik overgrip bij veel spelen.', 'Check materiaal- of allergieinformatie bij tapes.'],
    faq: [
      { question: 'Wanneer heb je overgrip nodig?', answer: 'Als de grip glad wordt, veel zweet opneemt of je extra comfort wilt.' },
      { question: 'Is grip belangrijker dan paddlemateriaal?', answer: 'Voor beginners vaak wel. Een paddle die goed vasthoudt speelt voorspelbaarder.' },
      { question: 'Zijn alle tapes hetzelfde?', answer: 'Nee, materiaal, dikte en gevoel verschillen. Claims blijven beperkt tot bevestigde productinformatie.' }
    ],
    internalLinks: [
      { href: '/collecties/tassen-accessoires/', label: 'Accessoires bekijken' },
      { href: '/pickleball-paddle-beginners/', label: 'Beginner paddle gids' },
      { href: '/productveiligheid/', label: 'Materiaalveiligheid' },
      ...coreLinks
    ],
    ctaLabel: 'Bekijk griproute'
  },
  {
    slug: '/pickleball-cadeau/',
    title: 'Pickleball cadeau of starterpakket',
    meta: 'Pickleball cadeau kiezen voor beginners: starterset, ballen, paddle of accessoires met keuzehulp.',
    keyword: 'pickleball cadeau',
    audience: 'Cadeaukopers, beginners en recreanten',
    cluster: 'startersets',
    buyerStage: 'solution-aware',
    intro: 'Een goed cadeau is niet automatisch de duurste paddle. Voor nieuwe spelers werkt een duidelijke starterroute meestal beter.',
    checklist: ['Kies starterset als de ontvanger nog niets heeft.', 'Kies ballen of grip als add-on.', 'Vermijd premium maatclaims zonder speelstijl te kennen.'],
    faq: [
      { question: 'Wat is een veilig pickleball cadeau?', answer: 'Een starterset of ballenset is meestal veiliger dan een premium paddle zonder speelstijl te kennen.' },
      { question: 'Kan ik een clubpakket cadeau doen?', answer: 'Voor groepen is een offerte of adviesaanvraag beter dan direct kopen.' },
      { question: 'Wanneer wordt cadeauverkoop actief?', answer: 'Pas wanneer voorraad, levering en retouren klaar zijn voor checkout.' }
    ],
    internalLinks: [
      { href: '/pickleball-starterset-kopen/', label: 'Starterset kiezen' },
      { href: '/pickleball-ballen-kopen/', label: 'Ballen als add-on' },
      { href: '/producten/', label: 'Producten bekijken' },
      ...coreLinks
    ],
    ctaLabel: 'Kies cadeau'
  },
  {
    slug: '/pickleball-bedrijven/',
    title: 'Pickleball voor bedrijven en teambuilding',
    meta: 'Materiaaladvies voor pickleball als bedrijfsactiviteit, clinic of teambuilding met sets, netten en ballen.',
    keyword: 'pickleball teambuilding',
    audience: 'Bedrijven, eventorganisatoren en sportcoaches',
    cluster: 'b2b-club-school',
    buyerStage: 'ready-to-quote',
    intro: 'Voor bedrijven draait het om eenvoudige opbouw, genoeg materiaal en een veilige groepsroute, niet om losse productvergelijking.',
    checklist: ['Bepaal aantal deelnemers en rondes.', 'Kies indoor/outdoor en beschikbare ruimte.', 'Vraag pakketadvies voor netten, ballen en paddles.'],
    faq: [
      { question: 'Is pickleball geschikt voor teambuilding?', answer: 'Ja, het is laagdrempelig en snel uit te leggen wanneer materiaal en veldopbouw kloppen.' },
      { question: 'Kunnen bedrijven direct bestellen?', answer: 'Bedrijfspakketten blijven quote-first totdat aantallen, locatie en logistiek bekend zijn.' },
      { question: 'Welke producten zijn nodig?', answer: 'Paddles, ballen, netten, belijning en soms reservegrips of tassen.' }
    ],
    internalLinks: [
      { href: '/pickleball-clinic-materiaal/', label: 'Clinic materiaal' },
      { href: '/pickleball-clubpakket/', label: 'Pakketadvies' },
      { href: '/pickleball-net-kopen/', label: 'Netten en veld' },
      ...coreLinks
    ],
    ctaLabel: 'Vraag bedrijfspakket'
  },
  {
    slug: '/pickleball-rotterdam/',
    title: 'Pickleball Rotterdam materiaaladvies',
    meta: 'Keuzehulp voor pickleball materiaal in Rotterdam: startersets, ballen, netten en clubpakketten als prelaunch route.',
    keyword: 'pickleball Rotterdam',
    audience: 'Rotterdamse spelers, clubs, scholen en organisatoren',
    cluster: 'geo-lokaal',
    buyerStage: 'local-research',
    intro: 'Rotterdam krijgt een lokale zoekroute voor spelers en organisaties die pickleballmateriaal willen voorbereiden zonder fysieke winkelclaim.',
    checklist: ['Gebruik de landelijke keuzehulp.', 'Vraag offerte bij clubs of scholen.', 'Geen lokale voorraadclaim zonder bewijs.'],
    faq: [
      { question: 'Heeft ProPickleball een winkel in Rotterdam?', answer: 'Nee. Dit is een lokale adviesroute, geen fysieke winkelpagina.' },
      { question: 'Welke producten zijn logisch voor Rotterdamse clubs?', answer: 'Startersets, ballen, netten, grips en clubpakketten zijn de eerste routes.' },
      { question: 'Kan levering naar Rotterdam later actief worden?', answer: 'Ja, zodra carrier-, voorraad- en checkout-readiness rond zijn.' }
    ],
    internalLinks: [
      { href: '/pickleball-nederland/', label: 'Nederland route' },
      { href: '/pickleball-voor-verenigingen/', label: 'Verenigingen' },
      { href: '/pickleball-clubpakket/', label: 'Clubpakket' },
      ...coreLinks
    ],
    ctaLabel: 'Vraag Rotterdam advies'
  },
  {
    slug: '/pickleball-amsterdam/',
    title: 'Pickleball Amsterdam materiaaladvies',
    meta: 'Keuzehulp voor pickleball materiaal in Amsterdam: startersets, paddles, ballen, netten en clubpakketten.',
    keyword: 'pickleball Amsterdam',
    audience: 'Amsterdamse spelers, clubs, scholen en recreatieorganisaties',
    cluster: 'geo-lokaal',
    buyerStage: 'local-research',
    intro: 'Amsterdam is een relevante zoekroute voor beginnende spelers en clubs, maar ProPickleball maakt geen fysieke winkel- of voorraadclaim.',
    checklist: ['Kies per speelplek.', 'Gebruik clubofferte bij groepen.', 'Bekijk retour- en serviceinformatie voordat checkout live gaat.'],
    faq: [
      { question: 'Heeft ProPickleball een winkel in Amsterdam?', answer: 'Nee. De pagina helpt lokale zoekers naar de juiste landelijke keuze- en offerteflow.' },
      { question: 'Welke route past bij Amsterdamse beginners?', answer: 'Starterset, regels, keuzehulp en ballen/netten afhankelijk van binnen of buiten spelen.' },
      { question: 'Kunnen scholen advies vragen?', answer: 'Ja, via de school- en gymlesroutes.' }
    ],
    internalLinks: [
      { href: '/pickleball-hoe-beginnen/', label: 'Beginnen met pickleball' },
      { href: '/pickleball-voor-scholen/', label: 'Scholen' },
      { href: '/pickleball-clubpakket/', label: 'Clubpakket' },
      ...coreLinks
    ],
    ctaLabel: 'Vraag Amsterdam advies'
  },
  {
    slug: '/wat-is-pickleball/',
    title: 'Wat is pickleball? Uitleg voor beginners',
    meta: 'Wat is pickleball? Korte uitleg van het spel, het veld, de paddle en de bal voor beginners in Nederland en Belgie.',
    keyword: 'wat is pickleball',
    audience: 'Beginners en nieuwsgierige starters',
    cluster: 'leren',
    buyerStage: 'problem-aware',
    intro: 'Pickleball is een racketsport op een klein veld met een paddle (dicht slagvlak zonder snaren) en een plastic bal met gaatjes. Het combineert elementen van tennis, badminton en tafeltennis en is makkelijk te leren, ook voor beginners en senioren.',
    checklist: ['Speel je binnen of buiten? Dat bepaalt de bal.', 'Begin met een controle-paddle en een starterset.', 'Leer de non-volley zone (kitchen) voordat je fanatiek wordt.'],
    faq: [
      { question: 'Is pickleball moeilijk te leren?', answer: 'Nee. De basis leer je in een uur: klein veld, lichte paddle en een langzame bal maken het toegankelijk voor beginners en alle leeftijden.' },
      { question: 'Wat heb je nodig om te beginnen?', answer: 'Een paddle, een pickleball (indoor of outdoor), een net of veld, en comfortabele sportschoenen. Een starterset regelt dit in een keer.' },
      { question: 'Wat is de kitchen of non-volley zone?', answer: 'Dat is de zone van 2,13 m aan weerszijden van het net waarin je de bal niet uit de lucht mag slaan. Het voorkomt smashen vlak bij het net.' }
    ],
    internalLinks: [
      { href: '/pickleball-spelregels/', label: 'Pickleball spelregels' },
      { href: '/pickleball-hoe-beginnen/', label: 'Beginnen met pickleball' },
      { href: '/pickleball-vs-padel/', label: 'Pickleball vs padel' },
      ...coreLinks
    ],
    ctaLabel: 'Start keuzehulp'
  },
  {
    slug: '/pickleball-vs-tennis/',
    title: 'Pickleball vs tennis: de verschillen',
    meta: 'Pickleball vs tennis: veldgrootte, paddle vs racket, bal, tempo en toegankelijkheid vergeleken voor wie wil starten.',
    keyword: 'pickleball vs tennis',
    audience: 'Tennissers en beginners die twijfelen',
    cluster: 'leren',
    buyerStage: 'problem-aware',
    intro: 'Pickleball wordt gespeeld op een veel kleiner veld dan tennis, met een paddle in plaats van een racket en een lichtere plastic bal. Daardoor is het rustiger op het lichaam en sneller te leren, terwijl tennis meer loopwerk en techniek vraagt.',
    checklist: ['Kleiner veld = minder loopwerk dan tennis.', 'Paddle is korter en lichter dan een tennisracket.', 'Tennisachtergrond? Kies een paddle met wat meer power.'],
    faq: [
      { question: 'Is pickleball makkelijker dan tennis?', answer: 'Voor de meeste mensen wel: het kleinere veld, de lichtere bal en de kortere paddle maken de basis sneller te leren dan tennis.' },
      { question: 'Kan ik als tennisser overstappen?', answer: 'Ja. Veel tennissers stappen makkelijk over. Let op de non-volley zone en kies eventueel een paddle met iets meer power die bij je slag past.' },
      { question: 'Is pickleball beter voor je gewrichten?', answer: 'Het kleinere veld en de lagere baltempo-belasting maken het vaak vriendelijker voor knieen en gewrichten dan tennis, maar goede schoenen blijven belangrijk.' }
    ],
    internalLinks: [
      { href: '/wat-is-pickleball/', label: 'Wat is pickleball?' },
      { href: '/pickleball-vs-padel/', label: 'Pickleball vs padel' },
      { href: '/pickleball-paddle-kopen/', label: 'Paddle kiezen' },
      ...coreLinks
    ],
    ctaLabel: 'Start keuzehulp'
  },
  {
    slug: '/welke-pickleball-paddle-past-bij-mij/',
    title: 'Welke pickleball paddle past bij mij?',
    meta: 'Welke pickleball paddle past bij jou? Kies op niveau, speelstijl (controle/power/spin), gewicht, grip en vorm met deze keuzehulp.',
    keyword: 'welke pickleball paddle',
    audience: 'Beginners en doorgroeiers die twijfelen',
    cluster: 'keuzehulp',
    buyerStage: 'solution-aware',
    intro: 'De juiste paddle hangt af van je niveau, speelstijl en gevoel. Beginners kiezen meestal controle en vergevingsgezindheid; wie meer ervaring heeft kan sturen op power of spin. Gewicht, gripmaat en vorm bepalen daarna het comfort.',
    checklist: ['Niveau eerst: beginner = controle en groot sweetspot.', 'Speelstijl: controle, power of spin.', 'Check gewicht, gripmaat en vorm voor comfort.'],
    faq: [
      { question: 'Welke paddle is het beste voor beginners?', answer: 'Een middelzware controle-paddle met een groot sweetspot en comfortabele grip is voor beginners meestal prettiger dan een uitgesproken power-paddle.' },
      { question: 'Wat betekent controle, power en spin?', answer: 'Controle geeft voorspelbaarheid en plaatsing, power geeft snelheid, spin helpt bij effect. Beginners hebben het meest aan controle.' },
      { question: 'Welk gewicht paddle moet ik kiezen?', answer: 'Lichter is wendbaarder en armvriendelijker, zwaarder geeft meer power maar vraagt meer kracht. Middenklasse is voor de meeste starters een veilige keuze.' }
    ],
    internalLinks: [
      { href: '/pickleball-paddle-beginners/', label: 'Paddle voor beginners' },
      { href: '/pickleball-paddle-kopen/', label: 'Paddle kopen' },
      { href: '/collecties/paddles/', label: 'Prelaunch paddles' },
      ...coreLinks
    ],
    ctaLabel: 'Doe de paddle keuzehulp'
  },
  {
    slug: '/pickleball-paddle-controle/',
    title: 'Pickleball paddle voor controle',
    meta: 'Control-paddles voor pickleball: meer plaatsing, voorspelbaarheid en een groot sweetspot. Ideaal voor beginners en netspel.',
    keyword: 'control paddle pickleball',
    audience: 'Beginners en spelers die op plaatsing spelen',
    cluster: 'paddles',
    buyerStage: 'solution-aware',
    intro: 'Een controle-paddle geeft je meer grip op de bal: voorspelbare plaatsing, een groot sweetspot en rust in het spel aan het net. Voor beginners en spelers die op techniek en plaatsing spelen is dit meestal de beste eerste keuze.',
    checklist: ['Groot sweetspot = vergevingsgezind bij mistiming.', 'Vaak middenklasse gewicht voor balans.', 'Prettig voor netspel en dinken.'],
    faq: [
      { question: 'Voor wie is een control-paddle?', answer: 'Voor beginners en voor spelers die winnen op plaatsing en consistentie in plaats van pure snelheid.' },
      { question: 'Lever ik power in met een control-paddle?', answer: 'Iets, maar je krijgt er voorspelbaarheid en een groter sweetspot voor terug. Veel spelers spelen daardoor juist beter.' },
      { question: 'Welk gewicht past bij controle?', answer: 'Middenklasse gewicht geeft meestal de beste balans tussen controle, comfort en genoeg body achter de bal.' }
    ],
    internalLinks: [
      { href: '/welke-pickleball-paddle-past-bij-mij/', label: 'Welke paddle past bij mij?' },
      { href: '/pickleball-paddle-power/', label: 'Paddle voor power' },
      { href: '/collecties/paddles/', label: 'Prelaunch paddles' },
      ...coreLinks
    ],
    ctaLabel: 'Doe de paddle keuzehulp'
  },
  {
    slug: '/pickleball-paddle-power/',
    title: 'Pickleball paddle voor power',
    meta: 'Power-paddles voor pickleball: meer snelheid en punch voor gevorderde spelers en wie van aanvallend spel houdt.',
    keyword: 'power paddle pickleball',
    audience: 'Gevorderde en aanvallende spelers',
    cluster: 'paddles',
    buyerStage: 'solution-aware',
    intro: 'Een power-paddle geeft meer snelheid en punch achter je slagen. Handig voor gevorderde spelers of wie van een tennisachtergrond komt en aanvallend speelt. Wel vraagt het meer controle van jezelf en een goede timing.',
    checklist: ['Meer snelheid, vraagt meer eigen controle.', 'Vaak iets zwaarder of met een stijver oppervlak.', 'Kies dit pas als je basis staat.'],
    faq: [
      { question: 'Is een power-paddle geschikt voor beginners?', answer: 'Meestal niet als eerste paddle: power gaat ten koste van vergevingsgezindheid. Begin liever met controle en groei later door.' },
      { question: 'Wanneer kies je power?', answer: 'Als je basis staat, je aanvallend speelt of van tennis komt en meer snelheid uit je slag wilt halen.' },
      { question: 'Power of controle?', answer: 'Twijfel je? Kies controle. Power lever je makkelijker in dan de rust en plaatsing die een control-paddle geeft.' }
    ],
    internalLinks: [
      { href: '/pickleball-paddle-controle/', label: 'Paddle voor controle' },
      { href: '/pickleball-paddle-spin/', label: 'Paddle voor spin' },
      { href: '/collecties/paddles/', label: 'Prelaunch paddles' },
      ...coreLinks
    ],
    ctaLabel: 'Doe de paddle keuzehulp'
  },
  {
    slug: '/pickleball-paddle-spin/',
    title: 'Pickleball paddle voor spin',
    meta: 'Spin-paddles voor pickleball: ruwer oppervlak voor meer effect en controle over je slagen. Voor spelers die met spin willen spelen.',
    keyword: 'spin paddle pickleball',
    audience: 'Gevorderde spelers die met effect spelen',
    cluster: 'paddles',
    buyerStage: 'solution-aware',
    intro: 'Een spin-paddle heeft een ruwer of getextureerd oppervlak waarmee je meer effect op de bal zet. Dat helpt bij aangesneden slagen, dinks met effect en meer controle over de baan van de bal. Vooral interessant voor gevorderde spelers.',
    checklist: ['Getextureerd oppervlak geeft grip op de bal.', 'Effect vraagt techniek en oefening.', 'Check of het oppervlak toernooi-goedgekeurd is als je competitie speelt.'],
    faq: [
      { question: 'Heb ik als beginner een spin-paddle nodig?', answer: 'Nee. Spin komt vooral tot zijn recht bij gevorderde techniek. Begin met controle en verken spin later.' },
      { question: 'Hoe werkt spin bij pickleball?', answer: 'Een ruwer oppervlak grijpt de bal beter, waardoor je met de juiste techniek meer effect kunt geven aan je slagen.' },
      { question: 'Is een spin-paddle toernooi-goedgekeurd?', answer: 'Dat verschilt per model. Voor toernooien check je de goedkeuringslijst; niet elk ruw oppervlak is toegestaan.' }
    ],
    internalLinks: [
      { href: '/pickleball-paddle-controle/', label: 'Paddle voor controle' },
      { href: '/pickleball-paddle-power/', label: 'Paddle voor power' },
      { href: '/collecties/paddles/', label: 'Prelaunch paddles' },
      ...coreLinks
    ],
    ctaLabel: 'Doe de paddle keuzehulp'
  },
  {
    slug: '/pickleball-paddle-lichtgewicht/',
    title: 'Lichtgewicht pickleball paddle',
    meta: 'Lichtgewicht pickleball paddles: wendbaar en armvriendelijk, prettig voor snelle reacties, senioren en wie last heeft van arm of elleboog.',
    keyword: 'lichtgewicht paddle pickleball',
    audience: 'Senioren, snelle netspelers, arm-gevoelige spelers',
    cluster: 'paddles',
    buyerStage: 'solution-aware',
    intro: 'Een lichtgewicht paddle is wendbaar en armvriendelijk: fijne, snelle reacties aan het net en minder belasting voor arm en elleboog. Prettig voor senioren en spelers die last hebben van hun arm, of die op snelheid van handelen spelen.',
    checklist: ['Wendbaar en snel aan het net.', 'Minder belasting voor arm/elleboog.', 'Iets minder body achter de bal dan een zwaardere paddle.'],
    faq: [
      { question: 'Voor wie is een lichtgewicht paddle?', answer: 'Voor senioren, spelers met arm- of elleboogklachten en wie op snelle reacties aan het net speelt.' },
      { question: 'Lever ik power in?', answer: 'Een beetje, maar je wint wendbaarheid en comfort. Voor veel spelers weegt dat zwaarder.' },
      { question: 'Wat is een goed gewicht?', answer: 'Lichtgewicht paddles zitten onderin het gewichtsbereik. Twijfel je, dan is middenklasse een veiligere allround keuze.' }
    ],
    internalLinks: [
      { href: '/welke-pickleball-paddle-past-bij-mij/', label: 'Welke paddle past bij mij?' },
      { href: '/pickleball-paddle-controle/', label: 'Paddle voor controle' },
      { href: '/collecties/paddles/', label: 'Prelaunch paddles' },
      ...coreLinks
    ],
    ctaLabel: 'Doe de paddle keuzehulp'
  },
  {
    slug: '/pickleball-zuid-holland/',
    title: 'Pickleball Zuid-Holland',
    meta: 'Regionale keuzehulp en offerte-intake voor pickleball in Zuid-Holland: Rotterdam, Den Haag, Leiden en Dordrecht.',
    keyword: 'pickleball Zuid-Holland',
    audience: 'Clubs, scholen en recreanten in Zuid-Holland',
    cluster: 'geo-lokaal',
    buyerStage: 'local-research',
    intro: 'Zuid-Holland (met Rotterdam en Den Haag) krijgt een regionale adviesroute voor scholen, clubs en recreatieorganisaties die pickleball willen opzetten. We adviseren over materiaal en houden verkoop gated tot alles klopt.',
    checklist: ['Regionale adviesroute, geen winkelpagina.', 'Stuur clubs en scholen naar offerte-intake.', 'Actuele speellocaties: check de bond of een locatiegids.'],
    faq: [
      { question: 'Staan hier speellocaties in Zuid-Holland?', answer: 'Nee. Dit is een regionale advies- en keuzehulppagina; voor actuele banen verwijzen we naar de pickleballbond en locatiegidsen, zodat de informatie klopt.' },
      { question: 'Voor wie is deze pagina?', answer: 'Scholen, sportverenigingen, recreatieparken en organisatoren die pickleball willen starten in Zuid-Holland.' },
      { question: 'Wanneer kan ik bestellen?', answer: 'Bestellen opent pas na leverancier-, verzend-, retour- en checkout-readiness.' }
    ],
    internalLinks: [
      { href: '/pickleball-rotterdam/', label: 'Pickleball Rotterdam' },
      { href: '/pickleball-voor-verenigingen/', label: 'Verenigingen' },
      { href: '/pickleball-voor-scholen/', label: 'Scholen' },
      ...coreLinks
    ],
    ctaLabel: 'Vraag Zuid-Holland advies'
  },
  {
    slug: '/pickleball-noord-holland/',
    title: 'Pickleball Noord-Holland',
    meta: 'Regionale keuzehulp en offerte-intake voor pickleball in Noord-Holland: Amsterdam, Haarlem en Alkmaar.',
    keyword: 'pickleball Noord-Holland',
    audience: 'Clubs, scholen en recreanten in Noord-Holland',
    cluster: 'geo-lokaal',
    buyerStage: 'local-research',
    intro: 'Noord-Holland (met Amsterdam als grootste hub) krijgt een regionale adviesroute voor scholen, clubs en recreatieorganisaties die pickleball willen introduceren, met materiaaladvies zonder direct grote voorraden.',
    checklist: ['Regionale adviesroute, geen winkelpagina.', 'Prioriteit voor scholen, clubs en clinics.', 'Actuele speellocaties: check de bond of een locatiegids.'],
    faq: [
      { question: 'Staan hier speellocaties in Noord-Holland?', answer: 'Nee. Dit is een regionale advies- en keuzehulppagina; voor actuele banen verwijzen we naar de bond en locatiegidsen.' },
      { question: 'Welke doelgroep krijgt prioriteit?', answer: 'Scholen, sportverenigingen, recreatieparken en introductieclinics in Noord-Holland.' },
      { question: 'Wanneer wordt levering actief?', answer: 'Pas na carrier-, supplier-, retour- en checkout-readiness.' }
    ],
    internalLinks: [
      { href: '/pickleball-amsterdam/', label: 'Pickleball Amsterdam' },
      { href: '/pickleball-voor-verenigingen/', label: 'Verenigingen' },
      { href: '/pickleball-voor-scholen/', label: 'Scholen' },
      ...coreLinks
    ],
    ctaLabel: 'Vraag Noord-Holland advies'
  },
  {
    slug: '/pickleball-utrecht/',
    title: 'Pickleball Utrecht',
    meta: 'Regionale keuzehulp en offerte-intake voor pickleball in de provincie Utrecht: Utrecht-stad en Amersfoort.',
    keyword: 'pickleball Utrecht',
    audience: 'Clubs, scholen en recreanten in Utrecht',
    cluster: 'geo-lokaal',
    buyerStage: 'local-research',
    intro: 'De provincie Utrecht krijgt een regionale adviesroute voor scholen, clubs en recreatieorganisaties die pickleball willen opzetten, met keuzehulp en offerte-intake in plaats van directe verkoop.',
    checklist: ['Regionale adviesroute, geen winkelpagina.', 'Stuur clubs en scholen naar offerte-intake.', 'Actuele speellocaties: check de bond of een locatiegids.'],
    faq: [
      { question: 'Staan hier speellocaties in Utrecht?', answer: 'Nee. Dit is een regionale advies- en keuzehulppagina; voor actuele banen verwijzen we naar de bond en locatiegidsen.' },
      { question: 'Voor wie is deze pagina?', answer: 'Scholen, sportverenigingen, recreatieparken en organisatoren in de provincie Utrecht.' },
      { question: 'Wanneer kan ik bestellen?', answer: 'Bestellen opent pas na leverancier-, verzend-, retour- en checkout-readiness.' }
    ],
    internalLinks: [
      { href: '/pickleball-hoe-beginnen/', label: 'Beginnen met pickleball' },
      { href: '/pickleball-voor-verenigingen/', label: 'Verenigingen' },
      { href: '/pickleball-voor-scholen/', label: 'Scholen' },
      ...coreLinks
    ],
    ctaLabel: 'Vraag Utrecht advies'
  },
  {
    slug: '/pickleball-gelderland/',
    title: 'Pickleball Gelderland',
    meta: 'Regionale keuzehulp en offerte-intake voor pickleball in Gelderland: Arnhem, Nijmegen en Apeldoorn.',
    keyword: 'pickleball Gelderland',
    audience: 'Clubs, scholen en recreanten in Gelderland',
    cluster: 'geo-lokaal',
    buyerStage: 'local-research',
    intro: 'Gelderland (met Arnhem en Nijmegen) krijgt een regionale adviesroute voor scholen, clubs en recreatieorganisaties die pickleball willen testen zonder direct grote voorraden te kopen.',
    checklist: ['Regionale adviesroute, geen winkelpagina.', 'Prioriteit voor scholen, clubs en clinics.', 'Actuele speellocaties: check de bond of een locatiegids.'],
    faq: [
      { question: 'Staan hier speellocaties in Gelderland?', answer: 'Nee. Dit is een regionale advies- en keuzehulppagina; voor actuele banen verwijzen we naar de bond en locatiegidsen.' },
      { question: 'Welke doelgroep krijgt prioriteit?', answer: 'Scholen, sportverenigingen, recreatieparken en introductieclinics in Gelderland.' },
      { question: 'Wanneer wordt levering actief?', answer: 'Pas na carrier-, supplier-, retour- en checkout-readiness.' }
    ],
    internalLinks: [
      { href: '/pickleball-hoe-beginnen/', label: 'Beginnen met pickleball' },
      { href: '/pickleball-voor-verenigingen/', label: 'Verenigingen' },
      { href: '/pickleball-voor-scholen/', label: 'Scholen' },
      ...coreLinks
    ],
    ctaLabel: 'Vraag Gelderland advies'
  },
  {
    slug: '/pickleball-limburg/',
    title: 'Pickleball Limburg',
    meta: 'Regionale keuzehulp en offerte-intake voor pickleball in Limburg: Maastricht, Venlo en Roermond.',
    keyword: 'pickleball Limburg',
    audience: 'Clubs, scholen en recreanten in Limburg',
    cluster: 'geo-lokaal',
    buyerStage: 'local-research',
    intro: 'Limburg krijgt een regionale adviesroute voor scholen, clubs en recreatieorganisaties die pickleball willen opzetten, met materiaaladvies en offerte-intake in plaats van directe verkoop.',
    checklist: ['Regionale adviesroute, geen winkelpagina.', 'Stuur clubs en scholen naar offerte-intake.', 'Actuele speellocaties: check de bond of een locatiegids.'],
    faq: [
      { question: 'Staan hier speellocaties in Limburg?', answer: 'Nee. Dit is een regionale advies- en keuzehulppagina; voor actuele banen verwijzen we naar de bond en locatiegidsen.' },
      { question: 'Voor wie is deze pagina?', answer: 'Scholen, sportverenigingen, recreatieparken en organisatoren in Limburg.' },
      { question: 'Wanneer kan ik bestellen?', answer: 'Bestellen opent pas na leverancier-, verzend-, retour- en checkout-readiness.' }
    ],
    internalLinks: [
      { href: '/pickleball-hoe-beginnen/', label: 'Beginnen met pickleball' },
      { href: '/pickleball-voor-verenigingen/', label: 'Verenigingen' },
      { href: '/pickleball-voor-scholen/', label: 'Scholen' },
      ...coreLinks
    ],
    ctaLabel: 'Vraag Limburg advies'
  },
  {
    slug: '/pickleball-eindhoven/',
    title: 'Pickleball Eindhoven',
    meta: 'Regionale keuzehulp en offerte-intake voor pickleball in Eindhoven en de Brainport-regio.',
    keyword: 'pickleball Eindhoven',
    audience: 'Clubs, scholen, bedrijven en recreanten in Eindhoven',
    cluster: 'geo-lokaal',
    buyerStage: 'local-research',
    intro: 'Eindhoven krijgt een lokale adviesroute voor scholen, clubs, bedrijven en recreanten die pickleball willen introduceren, met keuzehulp en offerte-intake in plaats van directe verkoop.',
    checklist: ['Lokale adviesroute, geen winkelpagina.', 'Ook interessant voor bedrijven (teamactiviteit).', 'Actuele speellocaties: check de bond of een locatiegids.'],
    faq: [
      { question: 'Staan hier speellocaties in Eindhoven?', answer: 'Nee. Dit is een lokale advies- en keuzehulppagina; voor actuele banen verwijzen we naar de bond en locatiegidsen.' },
      { question: 'Voor wie is deze pagina?', answer: 'Scholen, verenigingen, bedrijven en organisatoren in Eindhoven en omgeving.' },
      { question: 'Wanneer kan ik bestellen?', answer: 'Bestellen opent pas na leverancier-, verzend-, retour- en checkout-readiness.' }
    ],
    internalLinks: [
      { href: '/pickleball-brabant/', label: 'Pickleball Brabant' },
      { href: '/pickleball-bedrijven/', label: 'Voor bedrijven' },
      { href: '/pickleball-voor-verenigingen/', label: 'Verenigingen' },
      ...coreLinks
    ],
    ctaLabel: 'Vraag Eindhoven advies'
  },
  {
    slug: '/pickleball-den-haag/',
    title: 'Pickleball Den Haag',
    meta: 'Regionale keuzehulp en offerte-intake voor pickleball in Den Haag en omgeving.',
    keyword: 'pickleball Den Haag',
    audience: 'Clubs, scholen en recreanten in Den Haag',
    cluster: 'geo-lokaal',
    buyerStage: 'local-research',
    intro: 'Den Haag krijgt een lokale adviesroute voor scholen, clubs en recreatieorganisaties die pickleball willen opzetten, met materiaaladvies en offerte-intake in plaats van directe verkoop.',
    checklist: ['Lokale adviesroute, geen winkelpagina.', 'Stuur clubs en scholen naar offerte-intake.', 'Actuele speellocaties: check de bond of een locatiegids.'],
    faq: [
      { question: 'Staan hier speellocaties in Den Haag?', answer: 'Nee. Dit is een lokale advies- en keuzehulppagina; voor actuele banen verwijzen we naar de bond en locatiegidsen.' },
      { question: 'Voor wie is deze pagina?', answer: 'Scholen, sportverenigingen, recreatieparken en organisatoren in Den Haag en omgeving.' },
      { question: 'Wanneer kan ik bestellen?', answer: 'Bestellen opent pas na leverancier-, verzend-, retour- en checkout-readiness.' }
    ],
    internalLinks: [
      { href: '/pickleball-zuid-holland/', label: 'Pickleball Zuid-Holland' },
      { href: '/pickleball-voor-verenigingen/', label: 'Verenigingen' },
      { href: '/pickleball-voor-scholen/', label: 'Scholen' },
      ...coreLinks
    ],
    ctaLabel: 'Vraag Den Haag advies'
  },
  {
    slug: '/pickleball-paddle-gewicht-gripmaat/',
    title: 'Pickleball paddle: gewicht en gripmaat kiezen',
    meta: 'Welk gewicht en welke gripmaat pickleball paddle past bij jou? Uitleg over licht vs zwaar en de juiste grip voor comfort en controle.',
    keyword: 'pickleball paddle gewicht gripmaat',
    audience: 'Beginners en doorgroeiers die twijfelen over gevoel',
    cluster: 'keuzehulp',
    buyerStage: 'solution-aware',
    intro: 'Gewicht en gripmaat bepalen hoe een paddle aanvoelt. Lichter is wendbaarder en armvriendelijker, zwaarder geeft meer power. Een te grote of te kleine grip kost controle en comfort; kies de grip die net comfortabel in de hand ligt.',
    checklist: ['Licht = wendbaar/armvriendelijk, zwaar = power.', 'Middenklasse gewicht is een veilige allround keuze.', 'Grip: net comfortabel, niet te dik of te dun.'],
    faq: [
      { question: 'Welk gewicht paddle voor beginners?', answer: 'Een middenklasse gewicht geeft de beste balans tussen controle, comfort en genoeg body achter de bal.' },
      { question: 'Hoe kies ik de juiste gripmaat?', answer: 'Kies de grip die net comfortabel in je hand ligt; twijfel je, ga dan iets kleiner en bouw eventueel op met overgrip.' },
      { question: 'Kan ik de grip later aanpassen?', answer: 'Ja, met een overgrip maak je een grip iets dikker. Kleiner maken kan niet, dus kies liever niet te groot.' }
    ],
    internalLinks: [
      { href: '/welke-pickleball-paddle-past-bij-mij/', label: 'Welke paddle past bij mij?' },
      { href: '/pickleball-paddle-lichtgewicht/', label: 'Lichtgewicht paddle' },
      { href: '/collecties/paddles/', label: 'Prelaunch paddles' },
      ...coreLinks
    ],
    ctaLabel: 'Doe de paddle keuzehulp'
  },
  {
    slug: '/pickleball-veld-afmetingen/',
    title: 'Pickleball veld: afmetingen en opzet',
    meta: 'Pickleball veldafmetingen: hoe groot is een pickleballbaan, waar ligt de non-volley zone (kitchen) en hoe hoog hangt het net?',
    keyword: 'pickleball veld afmetingen',
    audience: 'Beginners, clubs en scholen die een veld opzetten',
    cluster: 'leren',
    buyerStage: 'problem-aware',
    intro: 'Een pickleballveld is 13,41 bij 6,10 meter (zelfde formaat als een badmintonbaan voor dubbelspel). Het net hangt in het midden op 86 cm en aan de zijkanten op 91 cm. De non-volley zone (kitchen) is 2,13 meter aan weerszijden van het net.',
    checklist: ['Veld: 13,41 x 6,10 m (dubbel badminton-formaat).', 'Net: 86 cm midden, 91 cm zijkant.', 'Kitchen: 2,13 m aan weerszijden van het net.'],
    faq: [
      { question: 'Hoe groot is een pickleballveld?', answer: 'Een pickleballveld is 13,41 bij 6,10 meter, hetzelfde als een badmintonbaan voor dubbelspel.' },
      { question: 'Hoe hoog hangt het pickleballnet?', answer: 'Het net hangt in het midden op 86 cm en aan de zijkanten op 91 cm.' },
      { question: 'Kan ik binnen op een badmintonbaan spelen?', answer: 'Ja. De maatvoering komt overeen met een badmintonbaan, dus veel sporthallen zijn geschikt met een draagbaar net.' }
    ],
    internalLinks: [
      { href: '/pickleball-spelregels/', label: 'Pickleball spelregels' },
      { href: '/wat-is-pickleball/', label: 'Wat is pickleball?' },
      { href: '/pickleball-clubpakket/', label: 'Materiaal voor clubs' },
      ...coreLinks
    ],
    ctaLabel: 'Start keuzehulp'
  },
  {
    slug: '/wat-kost-een-pickleball-starterset/',
    title: 'Wat kost een pickleball starterset?',
    meta: 'Wat kost een pickleball starterset? Uitleg over prijsklassen voor 2- en 4-speler sets en wat je voor je budget mag verwachten.',
    keyword: 'pickleball starterset prijs',
    audience: 'Beginners die een eerste set overwegen',
    cluster: 'koopintentie',
    buyerStage: 'ready-to-buy maar prelaunch gated',
    intro: 'Een pickleball starterset bevat meestal paddles, ballen en soms een net. De prijs hangt af van het aantal spelers, de kwaliteit van de paddles en of er een net bij zit. ProPickleball geeft richtprijzen en activeert checkout pas na leverancier- en voorraad-readiness.',
    checklist: ['Prijs hangt af van aantal spelers en paddle-kwaliteit.', 'Set met net is completer maar duurder.', 'Kijk naar wat je nodig hebt, niet alleen de laagste prijs.'],
    faq: [
      { question: 'Wat zit er in een starterset?', answer: 'Meestal 2 of 4 paddles, een aantal ballen en soms een draagbaar net. Precieze inhoud verschilt per set.' },
      { question: 'Kan ik nu al een set bestellen?', answer: 'Nee, ProPickleball staat in prelaunch. Je kunt richtprijzen bekijken, de set-builder gebruiken en een update aanvragen.' },
      { question: 'Wat is een goede set voor beginners?', answer: 'Een set met controle-paddles, de juiste bal voor je speelplek (indoor/outdoor) en eventueel een net om overal te spelen.' }
    ],
    internalLinks: [
      { href: '/pickleball-set-kopen/', label: 'Pickleball set kiezen' },
      { href: '/collecties/startersets/', label: 'Startersets' },
      { href: '/pickleball-kopen/', label: 'Algemene koopgids' },
      ...coreLinks
    ],
    ctaLabel: 'Bekijk startersets'
  },
  {
    slug: '/pickleball-voor-kinderen/',
    title: 'Pickleball voor kinderen',
    meta: 'Pickleball voor kinderen: waarom het geschikt is, welk materiaal past en hoe scholen en ouders kinderen laten starten.',
    keyword: 'pickleball voor kinderen',
    audience: 'Ouders, scholen en jeugdtrainers',
    cluster: 'leren',
    buyerStage: 'problem-aware',
    intro: 'Pickleball is geschikt voor kinderen: het kleine veld, de lichte paddle en de langzame bal maken het veilig en snel te leren. Voor de gymles of thuis werkt een lichte paddle en een indoor bal het prettigst.',
    checklist: ['Klein veld en lichte bal = veilig en toegankelijk.', 'Kies lichte paddles voor kleinere handen.', 'Indoor ballen voor gebruik in de gymzaal.'],
    faq: [
      { question: 'Vanaf welke leeftijd kunnen kinderen pickleball spelen?', answer: 'Veel kinderen kunnen vanaf een jaar of 6-8 meedoen; het kleine veld en de lichte bal maken het toegankelijk.' },
      { question: 'Welk materiaal voor kinderen?', answer: 'Een lichte paddle die goed in kleinere handen ligt en een indoor bal voor de gymzaal zijn een prettige start.' },
      { question: 'Is pickleball geschikt voor de gymles?', answer: 'Ja. Met draagbare netten en beginnerssets is het goed op te zetten in een sporthal.' }
    ],
    internalLinks: [
      { href: '/pickleball-voor-scholen/', label: 'Pickleball voor scholen' },
      { href: '/wat-is-pickleball/', label: 'Wat is pickleball?' },
      { href: '/pickleball-materiaal-gymles/', label: 'Materiaal voor gymles' },
      ...coreLinks
    ],
    ctaLabel: 'Vraag schooladvies'
  },
  {
    slug: '/pickleball-voor-senioren/',
    title: 'Pickleball voor senioren',
    meta: 'Pickleball voor senioren: waarom het populair is, welk materiaal gewrichtsvriendelijk is en hoe je veilig start.',
    keyword: 'pickleball voor senioren',
    audience: 'Senioren, 50-plus recreanten en clubs',
    cluster: 'leren',
    buyerStage: 'problem-aware',
    intro: 'Pickleball is populair bij senioren: het kleinere veld, de lichtere bal en het lagere tempo zijn vriendelijker voor de gewrichten dan tennis, terwijl je toch beweegt en sociaal bezig bent. Een lichtgewicht paddle en goede schoenen maken het comfortabel.',
    checklist: ['Kleiner veld = minder loopwerk en belasting.', 'Kies een lichtgewicht, armvriendelijke paddle.', 'Goede court-schoenen voorkomen blessures.'],
    faq: [
      { question: 'Is pickleball geschikt voor senioren?', answer: 'Ja. Het kleine veld en de lichte bal maken het gewrichtsvriendelijker dan veel andere racketsporten, terwijl je toch actief blijft.' },
      { question: 'Welke paddle voor senioren?', answer: 'Een lichtgewicht paddle is wendbaar en armvriendelijk, prettig voor snelle reacties zonder te veel belasting.' },
      { question: 'Waar kan ik als senior starten?', answer: 'Veel clubs en sporthallen bieden pickleball aan; met een starterset en draagbaar net kun je ook zelf beginnen.' }
    ],
    internalLinks: [
      { href: '/pickleball-paddle-lichtgewicht/', label: 'Lichtgewicht paddle' },
      { href: '/pickleball-vs-tennis/', label: 'Pickleball vs tennis' },
      { href: '/pickleball-hoe-beginnen/', label: 'Beginnen met pickleball' },
      ...coreLinks
    ],
    ctaLabel: 'Start keuzehulp'
  },
  {
    slug: '/beste-pickleball-paddle-vergelijken/',
    title: 'Pickleball paddles vergelijken: controle, power en spin',
    meta: 'Pickleball paddles vergelijken op speelstijl: controle, power en spin naast elkaar, plus gewicht en niveau, om de juiste te kiezen.',
    keyword: 'pickleball paddle vergelijken',
    audience: 'Doorgroeiers die twijfelen tussen speelstijlen',
    cluster: 'keuzehulp',
    buyerStage: 'solution-aware',
    intro: 'Paddles verschillen vooral in speelstijl: controle geeft plaatsing en rust, power geeft snelheid, spin geeft effect. Beginners kiezen meestal controle; gevorderden sturen op power of spin. Gewicht en gripmaat bepalen daarna het comfort.',
    checklist: ['Controle: plaatsing en groot sweetspot (beginners).', 'Power: snelheid, vraagt eigen controle.', 'Spin: effect via ruw oppervlak (gevorderd).'],
    faq: [
      { question: 'Controle, power of spin — wat kies ik?', answer: 'Twijfel je, kies controle: het is het meest vergevingsgezind. Power en spin komen tot hun recht als je basis staat.' },
      { question: 'Maakt gewicht veel uit?', answer: 'Ja. Lichter is wendbaar en armvriendelijk, zwaarder geeft meer power. Middenklasse is een veilige allround keuze.' },
      { question: 'Kan ik paddles echt vergelijken zonder te spelen?', answer: 'Specs helpen (gewicht, oppervlak, vorm), maar gevoel is persoonlijk. Onze keuzehulp filtert op niveau en speelstijl.' }
    ],
    internalLinks: [
      { href: '/pickleball-paddle-controle/', label: 'Paddle voor controle' },
      { href: '/pickleball-paddle-power/', label: 'Paddle voor power' },
      { href: '/pickleball-paddle-spin/', label: 'Paddle voor spin' },
      ...coreLinks
    ],
    ctaLabel: 'Doe de paddle keuzehulp'
  },
  {
    slug: '/leren/',
    title: 'Pickleball leren: de complete kennisbank',
    meta: 'Alles om pickleball te leren: wat is het, spelregels, veldafmetingen, paddle kiezen en pickleball vs padel/tennis. Overzicht voor beginners.',
    keyword: 'pickleball leren',
    audience: 'Beginners en nieuwsgierige starters',
    cluster: 'leren',
    buyerStage: 'problem-aware',
    intro: 'Nieuw met pickleball? In deze kennisbank vind je alles om te starten: wat pickleball is, de spelregels, de veldafmetingen, hoe je de juiste paddle kiest en hoe het zich verhoudt tot padel en tennis. Werk de gidsen door in je eigen tempo.',
    checklist: ['Begin bij "wat is pickleball" en de spelregels.', 'Kies daarna materiaal met de paddle-keuzehulp.', 'Twijfel je over de sport? Lees de vergelijkingen met padel en tennis.'],
    faq: [
      { question: 'Waar begin ik als complete beginner?', answer: 'Start met "wat is pickleball" en de spelregels, doe daarna de keuzehulp voor je eerste paddle en set.' },
      { question: 'Heb ik veel materiaal nodig om te leren?', answer: 'Nee. Een paddle, de juiste bal en een net of veld zijn genoeg. Een starterset regelt dit in een keer.' },
      { question: 'Kan ik pickleball zelf leren?', answer: 'De basis leer je snel zelf met deze gidsen; clubs en clinics helpen daarna met techniek en tactiek.' }
    ],
    internalLinks: [
      { href: '/wat-is-pickleball/', label: 'Wat is pickleball?' },
      { href: '/pickleball-spelregels/', label: 'Spelregels' },
      { href: '/pickleball-veld-afmetingen/', label: 'Veldafmetingen' },
      { href: '/pickleball-hoe-beginnen/', label: 'Beginnen met pickleball' },
      { href: '/welke-pickleball-paddle-past-bij-mij/', label: 'Welke paddle past bij mij?' },
      { href: '/pickleball-vs-padel/', label: 'Pickleball vs padel' },
      { href: '/pickleball-vs-tennis/', label: 'Pickleball vs tennis' },
      { href: '/pickleball-voor-kinderen/', label: 'Voor kinderen' },
      { href: '/pickleball-voor-senioren/', label: 'Voor senioren' },
      ...coreLinks
    ],
    ctaLabel: 'Start keuzehulp'
  },
  {
    slug: '/pickleball-schoenen-kopen/',
    title: 'Pickleball-schoenen kopen: waar op letten',
    meta: 'Pickleball-schoenen kiezen: waarom court-schoenen belangrijk zijn, verschil indoor/outdoor en waar je op let voor grip en steun.',
    keyword: 'pickleball schoenen',
    audience: 'Beginners en recreanten die veilig willen bewegen',
    cluster: 'keuzehulp',
    buyerStage: 'solution-aware',
    intro: 'Goede court-schoenen geven zijwaartse steun en grip die hardloopschoenen missen. Bij pickleball maak je veel snelle zijwaartse bewegingen; de juiste schoen voorkomt blessures. Kies indoor-zolen voor de zaal en outdoor-zolen voor buitenbanen.',
    checklist: ['Court-schoenen geven zijwaartse steun, hardloopschoenen niet.', 'Indoor-zool voor de zaal, outdoor-zool voor buiten.', 'Let op grip, demping en een stabiele pasvorm.'],
    faq: [
      { question: 'Kan ik hardloopschoenen gebruiken voor pickleball?', answer: 'Liever niet. Hardloopschoenen missen zijwaartse steun; court- of tennisschoenen zijn veiliger voor de snelle zijwaartse bewegingen.' },
      { question: 'Wat is het verschil tussen indoor- en outdoor-schoenen?', answer: 'Indoor-schoenen hebben een niet-markerende zool voor de zaal; outdoor-schoenen hebben een stevigere zool met meer grip voor buitenbanen.' },
      { question: 'Waar let ik op bij pickleball-schoenen?', answer: 'Zijwaartse steun, grip die past bij je ondergrond, goede demping en een stabiele, comfortabele pasvorm.' }
    ],
    internalLinks: [
      { href: '/pickleball-voor-senioren/', label: 'Pickleball voor senioren' },
      { href: '/wat-is-pickleball/', label: 'Wat is pickleball?' },
      { href: '/leren/', label: 'Kennisbank' },
      ...coreLinks
    ],
    ctaLabel: 'Start keuzehulp'
  },
  {
    slug: '/pickleball-dinken-techniek/',
    title: 'Dinken bij pickleball: de belangrijkste techniek',
    meta: 'Wat is dinken bij pickleball en waarom is het zo belangrijk? Uitleg van de dink-slag, de kitchen en hoe je het oefent als beginner.',
    keyword: 'pickleball dinken',
    audience: 'Beginners en doorgroeiers die willen verbeteren',
    cluster: 'leren',
    buyerStage: 'problem-aware',
    intro: 'Dinken is een zachte slag die je vanuit of vlak bij de non-volley zone (kitchen) net over het net speelt. Het is een van de belangrijkste technieken in pickleball: het dwingt je tegenstander omhoog te spelen en voorkomt dat die kan smashen. Controle is belangrijker dan kracht.',
    checklist: ['Dink = zachte, gecontroleerde slag net over het net.', 'Speel vanuit of vlak bij de kitchen-lijn.', 'Oefen controle en plaatsing, niet snelheid.'],
    faq: [
      { question: 'Wat is dinken bij pickleball?', answer: 'Een zachte, gecontroleerde slag die net over het net in de non-volley zone van de tegenstander landt, zodat die niet kan aanvallen.' },
      { question: 'Waarom is dinken belangrijk?', answer: 'Het houdt de bal laag en dwingt je tegenstander omhoog te spelen, waardoor smashen lastig wordt. Het is de basis van het geduldige netspel.' },
      { question: 'Hoe oefen ik dinken?', answer: 'Speel rustig heen en weer vlak bij de kitchen-lijn en focus op controle en plaatsing in plaats van snelheid.' }
    ],
    internalLinks: [
      { href: '/pickleball-spelregels/', label: 'Spelregels (kitchen)' },
      { href: '/pickleball-veld-afmetingen/', label: 'Veldafmetingen' },
      { href: '/pickleball-paddle-controle/', label: 'Controle-paddle' },
      ...coreLinks
    ],
    ctaLabel: 'Start keuzehulp'
  },
  {
    slug: '/pickleball-etiquette/',
    title: 'Pickleball-etiquette: ongeschreven regels',
    meta: 'Pickleball-etiquette voor beginners: hoe gedraag je je op de baan, roep je de score, en wat zijn de ongeschreven omgangsregels.',
    keyword: 'pickleball etiquette',
    audience: 'Beginners die net starten op een baan of club',
    cluster: 'leren',
    buyerStage: 'problem-aware',
    intro: 'Naast de spelregels kent pickleball ongeschreven omgangsregels die het spel leuk houden voor iedereen: roep de score duidelijk voor de service, wees eerlijk bij twijfel over in/uit, en houd rekening met spelers op naastgelegen banen.',
    checklist: ['Roep de score duidelijk voor elke service.', 'Bij twijfel over in/uit: gun het punt aan de tegenstander.', 'Wacht met oversteken tot naastgelegen rally klaar is.'],
    faq: [
      { question: 'Wie roept de score bij pickleball?', answer: 'De serverende partij roept de score (eigen-, tegenstander- en servernummer) duidelijk voordat de service wordt geslagen.' },
      { question: 'Wat doe ik bij twijfel of de bal in of uit was?', answer: 'Bij oprechte twijfel gun je het punt aan de tegenstander; eerlijkheid staat voorop in het recreatieve spel.' },
      { question: 'Hoe ga ik om met naastgelegen banen?', answer: 'Wacht met het ophalen of oversteken tot een naastgelegen rally klaar is, en geef ballen netjes terug.' }
    ],
    internalLinks: [
      { href: '/pickleball-spelregels/', label: 'Spelregels' },
      { href: '/wat-is-pickleball/', label: 'Wat is pickleball?' },
      { href: '/leren/', label: 'Kennisbank' },
      ...coreLinks
    ],
    ctaLabel: 'Start keuzehulp'
  },
  {
    slug: '/pickleball-net-opzetten/',
    title: 'Pickleball-net opzetten: hoogte en tips',
    meta: 'Pickleball-net opzetten: op welke hoogte hangt het net, hoe zet je een draagbaar net op en waar let je op voor thuis, park of club.',
    keyword: 'pickleball net opzetten',
    audience: 'Beginners, clubs en scholen die zelf een veld opzetten',
    cluster: 'leren',
    buyerStage: 'solution-aware',
    intro: 'Een pickleballnet hangt in het midden op 86 cm en aan de zijkanten op 91 cm. Een draagbaar net zet je in enkele minuten op: uitvouwen, spannen en de hoogte in het midden controleren. Ideaal voor thuis, in het park of op school.',
    checklist: ['Nethoogte: 86 cm midden, 91 cm zijkant.', 'Draagbaar net: uitvouwen, spannen, hoogte checken.', 'Voor clubs/scholen: stevigere of meerdere netten.'],
    faq: [
      { question: 'Hoe hoog hangt een pickleballnet?', answer: 'In het midden 86 cm en aan de zijkanten 91 cm. Controleer de midden-hoogte na het spannen.' },
      { question: 'Kan ik een draagbaar net gebruiken?', answer: 'Ja. Een draagbaar net is snel op te zetten en prettig voor thuis, park of gymles; voor intensief clubgebruik kies je een steviger net.' },
      { question: 'Waar zet ik een pickleballveld op?', answer: 'Op elke vlakke ondergrond met genoeg ruimte (13,41 x 6,10 m); veel badmintonbanen zijn geschikt.' }
    ],
    internalLinks: [
      { href: '/pickleball-veld-afmetingen/', label: 'Veldafmetingen' },
      { href: '/collecties/netten-en-belijning/', label: 'Netten' },
      { href: '/pickleball-clubpakket/', label: 'Voor clubs & scholen' },
      ...coreLinks
    ],
    ctaLabel: 'Bekijk netten'
  },
  {
    slug: '/pickleball-scoren-uitleg/',
    title: 'Pickleball scoren: hoe werkt het tellen?',
    meta: 'Pickleball scoren uitgelegd: alleen de serverende partij scoort, tot 11 punten met 2 verschil, en hoe je de score correct roept.',
    keyword: 'pickleball scoren',
    audience: 'Beginners die de scoreregels leren',
    cluster: 'leren',
    buyerStage: 'problem-aware',
    intro: 'Bij pickleball scoort alleen de serverende partij een punt. Een wedstrijd gaat meestal tot 11 punten met minimaal 2 punten verschil. Bij dubbelspel roep je drie getallen: je eigen score, die van de tegenstander en het servernummer (1 of 2).',
    checklist: ['Alleen de serverende partij scoort.', 'Tot 11 punten, met 2 punten verschil.', 'Dubbel: roep eigen score, tegenstander en servernummer.'],
    faq: [
      { question: 'Wie kan er punten scoren?', answer: 'Alleen de serverende partij. Wint de ontvangende partij de rally, dan krijgen zij de service (geen punt).' },
      { question: 'Tot hoeveel punten speel je?', answer: 'Meestal tot 11 punten met minimaal 2 punten verschil; bij toernooien soms tot 15 of 21.' },
      { question: 'Hoe roep ik de score bij dubbelspel?', answer: 'Drie getallen: je eigen score, die van de tegenstander en het servernummer (1 of 2), vóór elke service.' }
    ],
    internalLinks: [
      { href: '/pickleball-spelregels/', label: 'Spelregels' },
      { href: '/pickleball-etiquette/', label: 'Etiquette' },
      { href: '/wat-is-pickleball/', label: 'Wat is pickleball?' },
      ...coreLinks
    ],
    ctaLabel: 'Start keuzehulp'
  },
  {
    slug: '/pickleball-blessures-voorkomen/',
    title: 'Pickleball-blessures voorkomen',
    meta: 'Pickleball-blessures voorkomen: warming-up, goede court-schoenen, techniek en rust. Praktische tips voor veilig en langer spelen.',
    keyword: 'pickleball blessures voorkomen',
    audience: 'Recreanten en senioren die veilig willen spelen',
    cluster: 'leren',
    buyerStage: 'problem-aware',
    intro: 'De meeste pickleball-blessures komen door plotselinge zijwaartse bewegingen, verkeerde schoenen of geen warming-up. Met court-schoenen, een korte warming-up en aandacht voor techniek en rust speel je veiliger en langer.',
    checklist: ['Draag court-schoenen met zijwaartse steun.', 'Doe een korte warming-up voor je speelt.', 'Luister naar je lichaam; bouw intensiteit op.'],
    faq: [
      { question: 'Wat zijn veelvoorkomende pickleball-blessures?', answer: 'Vooral enkel-, knie- en achillespees-klachten door snelle zijwaartse bewegingen, plus tennis-elleboog bij verkeerde techniek of grip.' },
      { question: 'Hoe voorkom ik blessures?', answer: 'Goede court-schoenen, een warming-up, aandacht voor techniek en gripmaat, en voldoende rust tussen intensieve sessies.' },
      { question: 'Welke schoenen zijn veiliger?', answer: 'Court- of tennisschoenen met zijwaartse steun en grip die past bij je ondergrond; hardloopschoenen zijn minder geschikt.' }
    ],
    internalLinks: [
      { href: '/pickleball-schoenen-kopen/', label: 'Pickleball-schoenen' },
      { href: '/pickleball-voor-senioren/', label: 'Voor senioren' },
      { href: '/pickleball-dinken-techniek/', label: 'Techniek: dinken' },
      ...coreLinks
    ],
    ctaLabel: 'Start keuzehulp'
  },
  {
    slug: '/pickleball-merken/',
    title: 'Pickleball-merken: een overzicht',
    meta: 'Overzicht van bekende pickleball-merken en waar ze voor bekend staan, plus hoe je merkonafhankelijk de juiste paddle kiest.',
    keyword: 'pickleball merken',
    audience: 'Kopers die twijfelen tussen merken',
    cluster: 'keuzehulp',
    buyerStage: 'solution-aware',
    intro: 'Er zijn veel pickleball-merken, elk met een eigen focus op controle, power of spin. Belangrijker dan het merk is dat de paddle bij jouw niveau en speelstijl past. Wij adviseren merkonafhankelijk: eerst je speelstijl, dan het merk. ProPickleball staat in prelaunch en claimt geen merk-autorisaties.',
    checklist: ['Kies eerst speelstijl en niveau, dan pas een merk.', 'Elk merk heeft controle-, power- en spin-modellen.', 'Merkonafhankelijk advies voorkomt over-betalen voor een naam.'],
    faq: [
      { question: 'Welk pickleball-merk is het beste?', answer: 'Er is geen enkel "beste" merk; het hangt af van je speelstijl en niveau. Bijna elk groot merk heeft modellen voor controle, power en spin.' },
      { question: 'Moet ik een duur merk kopen als beginner?', answer: 'Nee. Beginners hebben vooral baat bij een controle-paddle met een groot sweetspot; dat hoeft geen premium merk te zijn.' },
      { question: 'Verkoopt ProPickleball premium merken?', answer: 'ProPickleball staat in prelaunch en toont geen merk-autorisaties of voorraad die er nog niet is; we helpen je eerst kiezen.' }
    ],
    internalLinks: [
      { href: '/welke-pickleball-paddle-past-bij-mij/', label: 'Welke paddle past bij mij?' },
      { href: '/beste-pickleball-paddle-vergelijken/', label: 'Paddles vergelijken' },
      { href: '/pickleball-paddle-controle/', label: 'Controle-paddle' },
      ...coreLinks
    ],
    ctaLabel: 'Doe de paddle keuzehulp'
  }
];

export function normalizeSeoPath(pathname: string): string {
  return pathname.endsWith('/') ? pathname : `${pathname}/`;
}

export function findSeoGuidePage(pathname: string): SeoGuidePage | null {
  const normalized = normalizeSeoPath(pathname);
  return seoGuidePages.find((page) => page.slug === normalized) ?? null;
}
